import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import PayoutBankDetailsCard from "../components/settings/PayoutBankDetailsCard";
import ClinicPageCard from "../components/settings/ClinicPageCard";
import ClinicalPrivacyCard, { hasClinicalSettings } from "../components/settings/ClinicalPrivacyCard";
import NoteTemplatesCard from "../components/settings/NoteTemplatesCard";
import PatientAlertsCard from "../components/settings/PatientAlertsCard";
import useFeatures from "../hooks/useFeatures";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import {
  Info,
  BellRing,
  Users,
  Brain,
  ShieldAlert,
  Clock,
  TrendingUp,
  Settings as SettingsIcon,
  Scale,
  Sparkles,
  Zap,
  Activity,
  Save,
  RotateCcw,
  Shield,
  FileText,
  Printer,
  UploadCloud,
  Image as ImageIcon,
  MapPin,
  Building2,
  Pencil,
  Trash2,
  Plus,
  X
} from "lucide-react";

// Component names mapping for each therapy type
const THERAPY_COMPONENT_NAMES = {
  CBT: {
    similarity_thresholds: {
      core_beliefs: "Core Beliefs",
      intermediate_beliefs: "Intermediate Beliefs",
      automatic_thoughts: "Automatic Thoughts",
      coping_strategies: "Coping Strategies",
    },
    component_weights: {
      core_beliefs: "Core Beliefs",
      intermediate_beliefs: "Intermediate Beliefs",
      automatic_thoughts: "Automatic Thoughts",
      coping_strategies: "Coping Strategies",
    },
  },
  REBT: {
    similarity_thresholds: {
      irrational_beliefs: "Irrational Beliefs",
      activating_events: "Activating Events (A)",
      consequences: "Consequences (C)",
      rational_alternatives: "Rational Alternatives",
    },
    component_weights: {
      irrational_beliefs: "Irrational Beliefs",
      activating_events: "Activating Events (A)",
      consequences: "Consequences (C)",
      rational_alternatives: "Rational Alternatives",
    },
  },
  SFBT: {
    similarity_thresholds: {
      exceptions: "Exceptions",
      strengths_resources: "Strengths & Resources",
      scaling_progress: "Scaling Progress",
      preferred_future: "Preferred Future/Goals",
    },
    component_weights: {
      exceptions: "Exceptions",
      strengths_resources: "Strengths & Resources",
      scaling_progress: "Scaling Progress",
      preferred_future: "Preferred Future/Goals",
    },
  },
};

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [myBranchId, setMyBranchId] = useState(null);
  const features = useFeatures();
  const [activeSection, setActiveSection] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("tab") || "General";
    } catch {
      return "General";
    }
  }); // "Scoring" | "General" | "Experience" | ...
  const [responseMode, setResponseMode] = useState("full"); // "streaming" | "full"
  const [verifierEnabled, setVerifierEnabled] = useState(false); // New verifier toggle
  const [ragRelevanceThreshold, setRagRelevanceThreshold] = useState(1.2); // RAG threshold for rubric queries
  const [ragDefaultThreshold, setRagDefaultThreshold] = useState(0.8); // Default RAG threshold
  const [includeTranscriptInReport, setIncludeTranscriptInReport] = useState(true); // [PRIVACY] Default True
  const [evaluationStandard, setEvaluationStandard] = useState("CTS-R"); // "CTS-R" | "NIMHANS"
  const [evaluationMode, setEvaluationMode] = useState("Academic"); // "Academic" | "Tele-MANAS"
  // Appointment & Payment Policies
  const [allowUnpaidBooking, setAllowUnpaidBooking] = useState(true);
  const [unpaidFeeDifferenceType, setUnpaidFeeDifferenceType] = useState("flat"); // "flat" | "percent"
  const [unpaidFeeDifferenceValue, setUnpaidFeeDifferenceValue] = useState(0);
  const [platformFeeEnabled, setPlatformFeeEnabled] = useState(false);
  const [platformFeeAmount, setPlatformFeeAmount] = useState(49);
  const [platformFeeWaived, setPlatformFeeWaived] = useState(true);
  const [maxAdvanceBookingDays, setMaxAdvanceBookingDays] = useState(30);
  const [maxOpenUnpaidBookings, setMaxOpenUnpaidBookings] = useState(2);
  const [unpaidReleaseHours, setUnpaidReleaseHours] = useState(2);
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [lateCancelWindow, setLateCancelWindow] = useState(24);
  const [lateFeeType, setLateFeeType] = useState("percent");
  const [lateFeeValue, setLateFeeValue] = useState(0);
  const [rescheduleCutoffHours, setRescheduleCutoffHours] = useState(0);
  const [allowSameDayChanges, setAllowSameDayChanges] = useState(false);
  const [followUpAdvanceNoticeDays, setFollowUpAdvanceNoticeDays] = useState(3);
  const [onlinePaymentEnabled, setOnlinePaymentEnabled] = useState(true);
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState({ card: true, netbanking: true, upi: true, wallet: true, emi: true, paylater: false });

  // Prescription & Clinic Address Settings
  const [hospitalName, setHospitalName] = useState("NL Labs Hospital");
  const [hospitalSlogan, setHospitalSlogan] = useState("General Medicine & Outpatient Clinical Care");
  const [hospitalAddress, setHospitalAddress] = useState("Medical Enclave, Sector 4, Main Clinic Road, New Delhi 110029");
  const [hospitalLatitude, setHospitalLatitude] = useState("28.5672");
  const [hospitalLongitude, setHospitalLongitude] = useState("77.2100");
  const [hospitalPhone, setHospitalPhone] = useState("+91 98765 43210");
  const [hospitalEmail, setHospitalEmail] = useState("care@mindconnecthealth.com");
  const [hospitalWebsite, setHospitalWebsite] = useState("www.mindconnecthealth.com");
  // Read-only - comes from the org record (set at onboarding), not editable here.
  const [orgGstin, setOrgGstin] = useState("");
  const [logoType, setLogoType] = useState("default"); // "default" | "custom"
  const [logoUrl, setLogoUrl] = useState(""); // stored value: "s3://<key>" for an uploaded logo, or a pasted https URL
  const [logoPreview, setLogoPreview] = useState(""); // loadable link (fresh presigned URL for an S3 logo)
  const [logoUploading, setLogoUploading] = useState(false);
  const isS3Logo = logoUrl.startsWith("s3://");
  const [defaultHideHeader, setDefaultHideHeader] = useState(false);
  const [savingPrescription, setSavingPrescription] = useState(false);

  // Branches
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchLocations, setBranchLocations] = useState({ states: [], cities: [] });
  const emptyBranchForm = {
    name: "", address: "", phone_number: "", state_id: "", city_id: "",
    hospital_name: "", slogan: "", email: "", website: "", logo_url: "",
    latitude: "", longitude: "", is_default: false,
    promo_active: false, promo_label: "", promo_discount_type: "percent",
    promo_discount_value: 0, promo_valid_until: "",
  };
  const [branchForm, setBranchForm] = useState(emptyBranchForm);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [savingBranch, setSavingBranch] = useState(false);

  // Experience & Complexity Settings
  const [expThresholdIntermediate, setExpThresholdIntermediate] = useState(3);
  const [expThresholdExpert, setExpThresholdExpert] = useState(8);
  const [expectationNovice, setExpectationNovice] = useState(55);
  const [expectationIntermediate, setExpectationIntermediate] = useState(65);
  const [expectationExpert, setExpectationExpert] = useState(75);
  const [complexityAdjustmentMedium, setComplexityAdjustmentMedium] = useState(5);
  const [complexityAdjustmentHigh, setComplexityAdjustmentHigh] = useState(10);
  const [phaseExpectations, setPhaseExpectations] = useState({}); // { "Phase Name": { "novice": 55, "intermediate": 65, "expert": 75 } }
  const [availablePhases, setAvailablePhases] = useState({}); // { "CBT": ["Phase1", ...], "REBT": [...], ... }
  const [phaseSettings, setPhaseSettings] = useState({}); // { "CBT": { "Phase1": { "mandatory": [], "weights": {} } } }
  const [phaseMetadata, setPhaseMetadata] = useState({}); // { "CBT": { "phases": { "Crisis": { "domains": { ... } } } } }
  const [selectedPhase, setSelectedPhase] = useState("");
  const [activeTab, setActiveTab] = useState("CBT");
  const [allSettings, setAllSettings] = useState({
    CBT: {
      accuracy_formula: "f1",
      similarity_thresholds: {
        core_beliefs: 0.8,
        intermediate_beliefs: 0.75,
        automatic_thoughts: 0.7,
        coping_strategies: 0.65,
      },
      component_weights: {
        core_beliefs: 0.35,
        intermediate_beliefs: 0.25,
        automatic_thoughts: 0.20,
        coping_strategies: 0.20,
      },
      use_semantic: false,
    },
    REBT: {
      accuracy_formula: "f1",
      similarity_thresholds: {
        irrational_beliefs: 0.75,
        activating_events: 0.7,
        consequences: 0.65,
        rational_alternatives: 0.7,
      },
      component_weights: {
        irrational_beliefs: 0.40,
        activating_events: 0.25,
        consequences: 0.25,
        rational_alternatives: 0.10,
      },
      use_semantic: false,
    },
    SFBT: {
      accuracy_formula: "f1",
      similarity_thresholds: {
        exceptions: 0.7,
        strengths_resources: 0.75,
        scaling_progress: 0.65,
        preferred_future: 0.7,
      },
      component_weights: {
        exceptions: 0.30,
        strengths_resources: 0.30,
        scaling_progress: 0.20,
        preferred_future: 0.20,
      },
      use_semantic: false,
    },
  });

  useEffect(() => {
    // Check if user is admin/ops
    const storedUser = localStorage.getItem("auth");
    let ownBranchAdmin = false;
    let ownBranchId = null;
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          const adminRoles = ["admin", "ops", "operations"];
          setIsAdmin(adminRoles.includes(role));
          // Deliberately separate from isAdmin: a branch admin must NOT get
          // the org-wide tabs (Scoring, Framework & Privacy, Experience,
          // General) this flag gates elsewhere on this page - only the
          // Branches tab checks isBranchAdmin, and only for their own branch.
          if (role === "branch admin") {
            ownBranchAdmin = true;
            ownBranchId = decoded.sub?.branch_id || null;
            setIsBranchAdmin(true);
            setMyBranchId(ownBranchId);
          }
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }

    // Load scoring settings + general settings from backend
    fetchSettings();
    fetchGeneralSettings();
    fetchAvailablePhases();
    fetchPhaseSettings();
    fetchPrescriptionHeaderSettings();
    // Pass these explicitly rather than reading the isBranchAdmin/myBranchId
    // state set just above - those setters won't have applied yet within
    // this same effect run, so the state would still read stale (false/null).
    fetchBranches(ownBranchAdmin, ownBranchId);
  }, []);

  useEffect(() => {
    if (activeTab) {
      const fetchData = async () => {
        try {
          // Fetch settings for values
          const res = await api.getEffectivePhaseSettings(activeTab);
          setPhaseSettings(prev => ({
            ...prev,
            [activeTab]: res.data
          }));

          // Fetch metadata for structure
          const metaRes = await api.getEffectivePhaseMetadata(activeTab);
          setPhaseMetadata(prev => ({
            ...prev,
            [activeTab]: metaRes.data
          }));

          const phases = Object.keys(res.data);
          if (phases.length > 0) {
            if (!selectedPhase || !phases.includes(selectedPhase)) {
              setSelectedPhase(phases[0]);
            }
          }
        } catch (err) {
          console.error(`Error fetching effective phase data for ${activeTab}:`, err);
        }
      };
      fetchData();
    }
  }, [activeTab]);

  const fetchPhaseSettings = async () => {
    try {
      const res = await api.getOrgConfig("phase_settings");
      if (res.data && res.data.config_data) {
        setPhaseSettings(res.data.config_data);
      }
    } catch (err) {
      console.error("Error fetching phase settings:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.getComparisonSettings();
      // API returns all therapy settings or single settings (backward compatibility)
      if (res.data.CBT || res.data.REBT || res.data.SFBT) {
        // New format: {CBT: {...}, REBT: {...}, SFBT: {...}}
        setAllSettings(res.data);
      } else {
        // Old format: single settings object (assume CBT)
        setAllSettings({
          CBT: res.data,
          REBT: allSettings.REBT, // Keep defaults
          SFBT: allSettings.SFBT, // Keep defaults
        });
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
      toast.error("Failed to load settings. Using default values.");
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralSettings = async () => {
    try {
      const res = await api.getGeneralSettings();
      const mode = res.data?.response_mode;
      const verifier = res.data?.verifier_enabled;
      const ragRelevance = res.data?.rag_relevance_threshold;
      const ragDefault = res.data?.rag_default_threshold;
      const evalStandard = res.data?.evaluation_standard;
      const evalMode = res.data?.evaluation_mode;

      if (mode === "streaming" || mode === "full") {
        setResponseMode(mode);
        localStorage.setItem("response_mode", mode);
      }
      if (verifier !== undefined) {
        setVerifierEnabled(verifier);
      }
      if (ragRelevance !== undefined) {
        setRagRelevanceThreshold(ragRelevance);
      }
      if (ragDefault !== undefined) {
        setRagDefaultThreshold(ragDefault);
      }
      if (evalStandard) {
        setEvaluationStandard(evalStandard);
      }
      if (evalMode) {
        setEvaluationMode(evalMode);
      }

      // Load Privacy Setting
      if (res.data?.include_transcript_in_report !== undefined) {
        setIncludeTranscriptInReport(res.data.include_transcript_in_report);
      } else {
        setIncludeTranscriptInReport(true); // Default
      }

      // Load Appointment & Payment Policies
      if (res.data?.allow_unpaid_booking !== undefined) {
        setAllowUnpaidBooking(Boolean(res.data.allow_unpaid_booking));
      }
      if (res.data?.unpaid_fee_difference_type) {
        setUnpaidFeeDifferenceType(res.data.unpaid_fee_difference_type);
      }
      if (res.data?.unpaid_fee_difference_value !== undefined) {
        setUnpaidFeeDifferenceValue(parseFloat(res.data.unpaid_fee_difference_value) || 0);
      }
      if (res.data?.platform_fee_enabled !== undefined) {
        setPlatformFeeEnabled(Boolean(res.data.platform_fee_enabled));
      }
      if (res.data?.platform_fee_amount !== undefined) {
        setPlatformFeeAmount(parseFloat(res.data.platform_fee_amount) || 0);
      }
      if (res.data?.platform_fee_waived !== undefined) {
        setPlatformFeeWaived(Boolean(res.data.platform_fee_waived));
      }
      if (res.data?.max_advance_booking_days !== undefined) {
        setMaxAdvanceBookingDays(parseInt(res.data.max_advance_booking_days, 10) || 30);
      }
      if (res.data?.cancellation_policy !== undefined) setCancellationPolicy(res.data.cancellation_policy || "");
      if (res.data?.late_cancel_window_hours !== undefined) setLateCancelWindow(res.data.late_cancel_window_hours);
      if (res.data?.late_cancel_fee_type) setLateFeeType(res.data.late_cancel_fee_type);
      if (res.data?.late_cancel_fee_value !== undefined) setLateFeeValue(res.data.late_cancel_fee_value);
      if (res.data?.unpaid_online_release_hours !== undefined) {
        setUnpaidReleaseHours(parseInt(res.data.unpaid_online_release_hours, 10));
      }
      if (res.data?.max_open_unpaid_bookings_per_phone !== undefined) {
        setMaxOpenUnpaidBookings(Math.max(1, parseInt(res.data.max_open_unpaid_bookings_per_phone, 10) || 2));
      }
      if (res.data?.allow_same_day_patient_changes !== undefined) setAllowSameDayChanges(!!res.data.allow_same_day_patient_changes);
      if (res.data?.reschedule_cutoff_hours !== undefined) {
        setRescheduleCutoffHours(Math.max(0, parseInt(res.data.reschedule_cutoff_hours, 10) || 0));
      }
      if (res.data?.follow_up_advance_notice_days !== undefined) {
        setFollowUpAdvanceNoticeDays(Math.max(0, parseInt(res.data.follow_up_advance_notice_days, 10) || 0));
      }
      if (res.data?.online_payment_enabled !== undefined) {
        setOnlinePaymentEnabled(Boolean(res.data.online_payment_enabled));
      }
      if (res.data?.allowed_payment_methods && typeof res.data.allowed_payment_methods === 'object') {
        setAllowedPaymentMethods(prev => ({ ...prev, ...res.data.allowed_payment_methods }));
      }

      // Load Experience & Complexity settings
      if (res.data?.exp_threshold_intermediate) setExpThresholdIntermediate(res.data.exp_threshold_intermediate);
      if (res.data?.exp_threshold_expert) setExpThresholdExpert(res.data.exp_threshold_expert);
      if (res.data?.expectation_novice) setExpectationNovice(res.data.expectation_novice);
      if (res.data?.expectation_intermediate) setExpectationIntermediate(res.data.expectation_intermediate);
      if (res.data?.expectation_expert) setExpectationExpert(res.data.expectation_expert);
      if (res.data?.complexity_adjustment_medium) setComplexityAdjustmentMedium(res.data.complexity_adjustment_medium);
      if (res.data?.complexity_adjustment_high) setComplexityAdjustmentHigh(res.data.complexity_adjustment_high);
      if (res.data?.phase_expectations) setPhaseExpectations(res.data.phase_expectations);
    } catch (err) {
      console.error("Error fetching general settings:", err);
      // Fall back to whatever is already in state/localStorage
    }
  };

  const fetchAvailablePhases = async () => {
    try {
      const res = await api.getAvailablePhases();
      setAvailablePhases(res.data || {});
    } catch (err) {
      console.error("Error fetching available phases:", err);
    }
  };

  const fetchPrescriptionHeaderSettings = async () => {
    try {
      const res = await api.getOrgConfig("prescription_header");
      if (res.data && res.data.config_data) {
        const d = res.data.config_data;
        if (d.hospital_name) setHospitalName(d.hospital_name);
        if (d.slogan) setHospitalSlogan(d.slogan);
        if (d.address) setHospitalAddress(d.address);
        if (d.latitude !== undefined && d.latitude !== null && d.latitude !== "") setHospitalLatitude(String(d.latitude));
        else if (d.lat !== undefined && d.lat !== null && d.lat !== "") setHospitalLatitude(String(d.lat));
        if (d.longitude !== undefined && d.longitude !== null && d.longitude !== "") setHospitalLongitude(String(d.longitude));
        else if (d.long !== undefined && d.long !== null && d.long !== "") setHospitalLongitude(String(d.long));
        if (d.phone) setHospitalPhone(d.phone);
        if (d.email) setHospitalEmail(d.email);
        if (d.website) setHospitalWebsite(d.website);
        if (d.logo_type) setLogoType(d.logo_type);
        if (d.logo_url) setLogoUrl(d.logo_url);
        setLogoPreview(d.logo_display_url || (d.logo_url && !String(d.logo_url).startsWith("s3://") ? d.logo_url : ""));
        if (d.default_hide_header !== undefined) setDefaultHideHeader(Boolean(d.default_hide_header));
        setOrgGstin(d.gstin || "");
        localStorage.setItem("org_prescription_header", JSON.stringify(d));
      }
    } catch (err) {
      const cached = localStorage.getItem("org_prescription_header");
      if (cached) {
        try {
          const d = JSON.parse(cached);
          if (d.hospital_name) setHospitalName(d.hospital_name);
          if (d.slogan) setHospitalSlogan(d.slogan);
          if (d.address) setHospitalAddress(d.address);
          if (d.latitude !== undefined && d.latitude !== null) setHospitalLatitude(String(d.latitude));
          else if (d.lat !== undefined && d.lat !== null) setHospitalLatitude(String(d.lat));
          if (d.longitude !== undefined && d.longitude !== null) setHospitalLongitude(String(d.longitude));
          else if (d.long !== undefined && d.long !== null) setHospitalLongitude(String(d.long));
          if (d.phone) setHospitalPhone(d.phone);
          if (d.email) setHospitalEmail(d.email);
          if (d.gstin) setOrgGstin(d.gstin);
          if (d.website) setHospitalWebsite(d.website);
          if (d.logo_type) setLogoType(d.logo_type);
          if (d.logo_url) setLogoUrl(d.logo_url);
          setLogoPreview(d.logo_display_url || (d.logo_url && !String(d.logo_url).startsWith("s3://") ? d.logo_url : ""));
          if (d.default_hide_header !== undefined) setDefaultHideHeader(Boolean(d.default_hide_header));
        } catch (e) {}
      }
    }
  };

  const fetchBranches = async (overrideIsBranchAdmin, overrideMyBranchId) => {
    setBranchesLoading(true);
    try {
      const [branchesRes, locationsRes] = await Promise.all([
        api.fetchOrgBranches(),
        api.getLocations(),
      ]);
      const fetched = branchesRes.data || [];
      setBranches(fetched);
      setBranchLocations(locationsRes.data || { states: [], cities: [] });

      // A branch admin only ever edits their own branch - land them
      // directly on it instead of a blank "Add Branch" form (which they
      // don't have access to anyway).
      const branchAdmin = overrideIsBranchAdmin ?? isBranchAdmin;
      const branchId = overrideMyBranchId ?? myBranchId;
      if (branchAdmin && branchId) {
        const own = fetched.find((b) => b.id === branchId);
        if (own) handleEditBranch(own);
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
      toast.error("Failed to load branch list.");
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleEditBranch = (branch) => {
    setEditingBranchId(branch.id);
    setBranchForm({
      name: branch.name || "",
      address: branch.address || "",
      phone_number: branch.phone_number || "",
      state_id: branch.state_id || "",
      city_id: branch.city_id || "",
      hospital_name: branch.hospital_name || "",
      slogan: branch.slogan || "",
      email: branch.email || "",
      website: branch.website || "",
      logo_url: branch.logo_url || "",
      latitude: branch.latitude ?? "",
      longitude: branch.longitude ?? "",
      is_default: Boolean(branch.is_default),
      promo_active: Boolean(branch.promo_active),
      promo_label: branch.promo_label || "",
      promo_discount_type: branch.promo_discount_type || "percent",
      promo_discount_value: branch.promo_discount_value ?? 0,
      promo_valid_until: branch.promo_valid_until || "",
    });
  };

  const handleCancelBranchEdit = () => {
    setEditingBranchId(null);
    setBranchForm(emptyBranchForm);
  };

  const handleSaveBranch = async () => {
    if (!isAdmin) {
      toast.error("Only the organization admin can manage branches.");
      return;
    }
    if (!branchForm.name.trim()) {
      toast.error("Please provide a branch name.");
      return;
    }

    setSavingBranch(true);
    try {
      if (editingBranchId) {
        await api.updateOrgBranch(editingBranchId, branchForm);
        toast.success("Branch updated successfully!");
      } else {
        await api.createOrgBranch(branchForm);
        toast.success("Branch added successfully!");
      }
      handleCancelBranchEdit();
      await fetchBranches();
    } catch (err) {
      console.error("Error saving branch:", err);
      toast.error(err.response?.data?.error || "Failed to save branch.");
    } finally {
      setSavingBranch(false);
    }
  };

  const handleDeleteBranch = async (id) => {
    if (!isAdmin) {
      toast.error("Only the organization admin can manage branches.");
      return;
    }
    if (!window.confirm("Delete this branch? Existing shifts/appointments that reference it will keep showing its saved details.")) return;

    try {
      await api.deleteOrgBranch(id);
      toast.success("Branch deleted.");
      if (editingBranchId === id) handleCancelBranchEdit();
      await fetchBranches();
    } catch (err) {
      console.error("Error deleting branch:", err);
      toast.error(err.response?.data?.error || "Failed to delete branch.");
    }
  };

  const availableBranchCities = branchLocations.cities.filter(
    (c) => !branchForm.state_id || c.state_id?.toString() === branchForm.state_id?.toString()
  );

  const handleSavePrescriptionSettings = async () => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }
    try {
      setSavingPrescription(true);
      const parsedLat = hospitalLatitude !== "" ? parseFloat(hospitalLatitude) : null;
      const parsedLong = hospitalLongitude !== "" ? parseFloat(hospitalLongitude) : null;
      const payload = {
        hospital_name: hospitalName.trim(),
        slogan: hospitalSlogan.trim(),
        address: hospitalAddress.trim(),
        latitude: parsedLat,
        longitude: parsedLong,
        lat: parsedLat,
        long: parsedLong,
        phone: hospitalPhone.trim(),
        email: hospitalEmail.trim(),
        website: hospitalWebsite.trim(),
        logo_type: logoType,
        logo_url: logoUrl.trim(),
        default_hide_header: Boolean(defaultHideHeader)
      };
      await api.updateOrgConfig("prescription_header", payload);
      // gstin isn't part of the saved config (read-only, owned by the org
      // record) - kept in the cache so printed prescriptions still show it.
      localStorage.setItem("org_prescription_header", JSON.stringify({ ...payload, gstin: orgGstin || null, logo_display_url: logoPreview || null }));
      toast.success("Address & Prescription settings saved successfully!");
    } catch (err) {
      console.error("Failed to save address and prescription settings:", err);
      toast.error("Failed to save address and prescription settings.");
    } finally {
      setSavingPrescription(false);
    }
  };

  // Uploads to S3 (backend validates type/size); the letterhead stores the
  // returned "s3://" reference once Save is clicked.
  const handleLogoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      toast.error("The logo must be 1 MB or smaller.");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await api.uploadLetterheadLogo(fd);
      setLogoUrl(res.data.logo_url);
      setLogoPreview(res.data.logo_display_url || "");
      setLogoType("custom");
      toast.success("Logo uploaded. Click 'Save Letterhead Settings' to use it.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't upload the logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveAllConfig = async () => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }

    try {
      setSaving(true);

      // 1. Validate all therapy types
      for (const therapyType of ["CBT", "REBT", "SFBT"]) {
        const weights = allSettings[therapyType].component_weights;
        const total = Object.values(weights).reduce((sum, val) => sum + parseFloat(val), 0);
        if (Math.abs(total - 1.0) > 0.01) {
          toast.error(`${therapyType} component weights must sum to 1.0 (currently ${total.toFixed(2)})`);
          setSaving(false);
          return;
        }
      }

      // 2. Save General Settings (Benchmarks, Experience, Phases, Concessions)
      // Build clean phase_expectations: only valid phase names from availablePhases,
      // defaulting to 100 for phases not yet explicitly set. This strips stale keys
      // (e.g. evaluation standard names like "CTS-R"/"NIMHANS") that may be in state.
      const validPhaseNames = new Set(Object.values(availablePhases).flat());
      const cleanPhaseExpectations = {};
      validPhaseNames.forEach(phase => {
        cleanPhaseExpectations[phase] = phaseExpectations[phase] ?? 100;
      });

      await api.updateGeneralSettings({
        response_mode: responseMode,
        verifier_enabled: verifierEnabled,
        rag_relevance_threshold: ragRelevanceThreshold,
        rag_default_threshold: ragDefaultThreshold,
        include_transcript_in_report: includeTranscriptInReport,
        allow_unpaid_booking: allowUnpaidBooking,
        unpaid_fee_difference_type: unpaidFeeDifferenceType,
        unpaid_fee_difference_value: parseFloat(unpaidFeeDifferenceValue) || 0,
        platform_fee_enabled: platformFeeEnabled,
        platform_fee_amount: Math.max(0, parseFloat(platformFeeAmount) || 0),
        platform_fee_waived: platformFeeWaived,
        max_advance_booking_days: Math.max(1, parseInt(maxAdvanceBookingDays, 10) || 1),
        max_open_unpaid_bookings_per_phone: Math.max(1, parseInt(maxOpenUnpaidBookings, 10) || 1),
        unpaid_online_release_hours: parseInt(unpaidReleaseHours, 10),
        cancellation_policy: cancellationPolicy,
        late_cancel_window_hours: Math.max(0, parseInt(lateCancelWindow, 10) || 0),
        late_cancel_fee_type: lateFeeType,
        late_cancel_fee_value: Math.max(0, parseFloat(lateFeeValue) || 0),
        reschedule_cutoff_hours: Math.max(0, parseInt(rescheduleCutoffHours, 10) || 0),
        allow_same_day_patient_changes: allowSameDayChanges,
        follow_up_advance_notice_days: Math.max(0, parseInt(followUpAdvanceNoticeDays, 10) || 0),
        online_payment_enabled: onlinePaymentEnabled,
        allowed_payment_methods: allowedPaymentMethods,
        exp_threshold_intermediate: expThresholdIntermediate,
        exp_threshold_expert: expThresholdExpert,
        expectation_novice: expectationNovice,
        expectation_intermediate: expectationIntermediate,
        expectation_expert: expectationExpert,
        complexity_adjustment_medium: complexityAdjustmentMedium,
        complexity_adjustment_high: complexityAdjustmentHigh,
        phase_expectations: cleanPhaseExpectations
      });

      // 3. Sequentially save each therapy type (avoid race conditions in DB)
      // We do this because therapists settings are in the same comparison_settings JSON
      await api.updateComparisonSettings("CBT", allSettings.CBT);

      // Only save REBT/SFBT if they have phases (indicates they are in use/active)
      if (availablePhases.REBT && availablePhases.REBT.length > 0) {
        await api.updateComparisonSettings("REBT", allSettings.REBT);
      }
      if (availablePhases.SFBT && availablePhases.SFBT.length > 0) {
        await api.updateComparisonSettings("SFBT", allSettings.SFBT);
      }

      toast.success("All configuration changes saved successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Error saving configuration:", err);
      toast.error(err.response?.data?.error || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }

    // Validate all therapy types
    for (const therapyType of ["CBT", "REBT", "SFBT"]) {
      const weights = allSettings[therapyType].component_weights;
      const total = Object.values(weights).reduce((sum, val) => sum + parseFloat(val), 0);
      if (Math.abs(total - 1.0) > 0.01) {
        toast.error(`${therapyType} component weights must sum to 1.0 (currently ${total.toFixed(2)})`);
        return;
      }
    }

    try {
      setSaving(true);
      // Save each therapy type
      await Promise.all([
        api.updateComparisonSettings("CBT", allSettings.CBT),
        api.updateComparisonSettings("REBT", allSettings.REBT),
        api.updateComparisonSettings("SFBT", allSettings.SFBT),
      ]);
      toast.success("All therapy settings saved successfully!");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error(err.response?.data?.error || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (therapyType) => {
    if (window.confirm(`Reset ${therapyType} settings to default values?`)) {
      const defaults = {
        CBT: {
          accuracy_formula: "f1",
          similarity_thresholds: {
            core_beliefs: 0.8,
            intermediate_beliefs: 0.75,
            automatic_thoughts: 0.7,
            coping_strategies: 0.65,
          },
          component_weights: {
            core_beliefs: 0.35,
            intermediate_beliefs: 0.25,
            automatic_thoughts: 0.20,
            coping_strategies: 0.20,
          },
          use_semantic: false,
        },
        REBT: {
          accuracy_formula: "f1",
          similarity_thresholds: {
            irrational_beliefs: 0.75,
            activating_events: 0.7,
            consequences: 0.65,
            rational_alternatives: 0.7,
          },
          component_weights: {
            irrational_beliefs: 0.40,
            activating_events: 0.25,
            consequences: 0.25,
            rational_alternatives: 0.10,
          },
          use_semantic: false,
        },
        SFBT: {
          accuracy_formula: "f1",
          similarity_thresholds: {
            exceptions: 0.7,
            strengths_resources: 0.75,
            scaling_progress: 0.65,
            preferred_future: 0.7,
          },
          component_weights: {
            exceptions: 0.30,
            strengths_resources: 0.30,
            scaling_progress: 0.20,
            preferred_future: 0.20,
          },
          use_semantic: false,
        },
      };

      setAllSettings((prev) => ({
        ...prev,
        [therapyType]: defaults[therapyType],
      }));
      toast.info(`${therapyType} settings reset to defaults. Click Save to apply.`);
    }
  };

  const updateSetting = (therapyType, path, value) => {
    setAllSettings((prev) => {
      const keys = path.split(".");
      const newSettings = { ...prev };
      const therapySettings = { ...newSettings[therapyType] };

      let current = therapySettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return {
        ...newSettings,
        [therapyType]: therapySettings,
      };
    });
  };

  const updatePhaseSetting = (therapyType, phase, path, value) => {
    setPhaseSettings((prev) => {
      const newPhaseSettings = { ...prev };
      const therapyData = { ...(newPhaseSettings[therapyType] || {}) };
      const phaseData = { ...(therapyData[phase] || {}) };

      // Consolidate into standard keys from any existing names
      const weights = { ...(phaseData.weights || phaseData.competency_weights || {}) };
      const mandatory = [...(phaseData.mandatory || phaseData.important || [])];

      if (path === "mandatory") {
        const competency = value.competency;
        const isMandatory = value.checked;

        if (isMandatory) {
          if (!mandatory.includes(competency)) mandatory.push(competency);
        } else {
          const index = mandatory.indexOf(competency);
          if (index !== -1) mandatory.splice(index, 1);
          // Also set weight to 0 if de-selected
          weights[competency] = 0;
        }
      } else if (path.startsWith("weights.")) {
        const competency = path.split(".")[1];
        weights[competency] = value;
      }

      // Update with standardized keys and clean up legacy ones
      const updatedPhaseData = {
        ...(phaseData || {}),
        mandatory,
        weights
      };
      delete updatedPhaseData.important;
      delete updatedPhaseData.competency_weights;

      therapyData[phase] = updatedPhaseData;
      newPhaseSettings[therapyType] = therapyData;
      return newPhaseSettings;
    });
  };

  const handleSavePhaseConfig = async (therapyType) => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }

    const currentPhaseData = phaseSettings[therapyType]?.[selectedPhase];
    if (!currentPhaseData) return;

    // Support both 'competency_weights' (new) and 'weights' (from JSON defaults)
    const weights = currentPhaseData.competency_weights || currentPhaseData.weights || {};
    const total = Object.values(weights).reduce((sum, val) => sum + parseFloat(val || 0), 0);

    if (Math.abs(total - 1.0) > 0.01) {
      toast.error(`Weights for ${therapyType} - ${selectedPhase} must sum to 1.0 (currently ${total.toFixed(2)})`);
      return;
    }

    try {
      setSaving(true);
      // We save the entire phaseSettings object for this therapy type
      const payload = {
        ...phaseSettings
      };
      await api.updateOrgConfig("phase_settings", payload);
      toast.success(`${therapyType} phase settings saved successfully!`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error(`Error saving ${therapyType} phase settings:`, err);
      toast.error(err.response?.data?.error || `Failed to save ${therapyType} phase settings.`);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPhaseScoring = async () => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can reset settings.");
      return;
    }

    if (!window.confirm(
      "Reset all phase scoring weights to system defaults?\n\n" +
      "This will remove all custom competency weights and mandatory selections for ALL phases. " +
      "The system will use the built-in defaults from the JSON configuration.\n\n" +
      "This action cannot be undone."
    )) {
      return;
    }

    try {
      setSaving(true);
      const res = await api.resetPhaseSettings();
      toast.success(res.data?.message || "Scoring reset to system defaults!");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Error resetting phase settings:", err);
      toast.error(err.response?.data?.error || "Failed to reset scoring settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGeneral = async () => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }

    try {
      setSaving(true);
      await api.updateGeneralSettings({
        response_mode: responseMode,
        verifier_enabled: verifierEnabled,
        rag_relevance_threshold: ragRelevanceThreshold,
        rag_default_threshold: ragDefaultThreshold,
        include_transcript_in_report: includeTranscriptInReport,
        allow_unpaid_booking: allowUnpaidBooking,
        unpaid_fee_difference_type: unpaidFeeDifferenceType,
        unpaid_fee_difference_value: parseFloat(unpaidFeeDifferenceValue) || 0,
        platform_fee_enabled: platformFeeEnabled,
        platform_fee_amount: Math.max(0, parseFloat(platformFeeAmount) || 0),
        platform_fee_waived: platformFeeWaived,
        max_advance_booking_days: Math.max(1, parseInt(maxAdvanceBookingDays, 10) || 1),
        max_open_unpaid_bookings_per_phone: Math.max(1, parseInt(maxOpenUnpaidBookings, 10) || 1),
        unpaid_online_release_hours: parseInt(unpaidReleaseHours, 10),
        cancellation_policy: cancellationPolicy,
        late_cancel_window_hours: Math.max(0, parseInt(lateCancelWindow, 10) || 0),
        late_cancel_fee_type: lateFeeType,
        late_cancel_fee_value: Math.max(0, parseFloat(lateFeeValue) || 0),
        reschedule_cutoff_hours: Math.max(0, parseInt(rescheduleCutoffHours, 10) || 0),
        allow_same_day_patient_changes: allowSameDayChanges,
        follow_up_advance_notice_days: Math.max(0, parseInt(followUpAdvanceNoticeDays, 10) || 0),
        online_payment_enabled: onlinePaymentEnabled,
        allowed_payment_methods: allowedPaymentMethods,
        evaluation_standard: evaluationStandard,
        evaluation_mode: evaluationMode,
        exp_threshold_intermediate: expThresholdIntermediate,
        exp_threshold_expert: expThresholdExpert,
        expectation_novice: expectationNovice,
        expectation_intermediate: expectationIntermediate,
        expectation_expert: expectationExpert,
        complexity_adjustment_medium: complexityAdjustmentMedium,
        complexity_adjustment_high: complexityAdjustmentHigh,
        phase_expectations: phaseExpectations
      });
      toast.success("General settings saved successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Error saving general settings:", err);
      toast.error(err.response?.data?.error || "Failed to save general settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (therapyType) => {
    if (!isAdmin) {
      toast.error("Only admin/ops users can update settings.");
      return;
    }

    const settings = allSettings[therapyType];
    const weights = settings.component_weights;
    const total = Object.values(weights).reduce((sum, val) => sum + parseFloat(val), 0);

    if (Math.abs(total - 1.0) > 0.01) {
      toast.error(`${therapyType} component weights must sum to 1.0 (currently ${total.toFixed(2)})`);
      return;
    }

    try {
      setSaving(true);
      await api.updateComparisonSettings(therapyType, settings);
      toast.success(`${therapyType} settings saved successfully!`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error(`Error saving ${therapyType} settings:`, err);
      toast.error(err.response?.data?.error || `Failed to save ${therapyType} settings.`);
    } finally {
      setSaving(false);
    }
  };

  const renderTherapySettings = (therapyType) => {
    const settings = allSettings[therapyType];
    const componentNames = THERAPY_COMPONENT_NAMES[therapyType];
    const isActive = activeTab === therapyType;

    if (!isActive) return null;

    return (
      <div className="space-y-6">
        {/* <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <label className="text-base md:text-lg font-bold text-gray-700">
              Accuracy Formula
            </label>
            <div className="relative group">
              <Info className="w-5 h-5 text-gray-400 cursor-help hover:text-gray-600" />
              <div className="absolute z-10 w-96 p-4 mt-2 text-sm text-white bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 z-50">
                <p className="font-bold mb-3 text-blue-300">The "Grading" Step</p>
                <p className="mb-2">
                  This decides <strong>HOW</strong> to calculate the score after we know which items matched.
                </p>

                <div className="bg-gray-700 p-2 rounded text-xs italic text-gray-300 mb-3 border-l-2 border-blue-400">
                  "Okay, they matched 3 items but missed 2 and guessed 1 wrong. What grade do they get?"
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="font-bold text-green-300 block">Precision</span>
                    <span className="text-xs text-gray-300">Grades heavily on <strong>correctness</strong> (matches / total guesses). Good if you want to penalize wild guessing.</span>
                  </div>
                  <div>
                    <span className="font-bold text-yellow-300 block">Jaccard (Set Similarity)</span>
                    <div>
                      <span className="font-bold text-purple-300 block">Recall (Concept)</span>
                      <span className="text-xs text-gray-300">Grades heavily on <strong>completeness</strong> (matches / total actual items). Good if finding everything is important.</span>
                    </div>
                    <div>
                      <span className="font-bold text-blue-300 block">F1 Score (Default)</span>
                      <span className="text-xs text-gray-300">Balances both. You have to find most items <strong>AND</strong> not make too many wrong guesses.</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 border-4 border-transparent border-b-gray-800"></div>
              </div>
            </div>
          </div>
          <select
            value={settings.accuracy_formula}
            onChange={(e) => updateSetting(therapyType, "accuracy_formula", e.target.value)}
            disabled={!isAdmin}
            className="w-full px-4 py-3 text-base md:text-lg border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          >
            <option value="f1">F1 Score (Balanced - Default)</option>
            <option value="precision">Precision</option>
            <option value="jaccard">Jaccard Similarity</option>
          </select>
        </div> */}

        {/* <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <label className="text-base md:text-lg font-bold text-gray-700">
              Similarity Thresholds (0.0 - 1.0)
            </label>
            <div className="relative group">
              <Info className="w-5 h-5 text-gray-400 cursor-help hover:text-gray-600" />
              <div className="absolute z-10 w-96 p-4 mt-2 text-sm text-white bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 z-50">
                <p className="font-bold mb-3 text-blue-300">"Strictness Dial" for Grading</p>

                <div className="space-y-2 mb-4">
                  <p>
                    <span className="font-semibold text-green-300">High (e.g., 0.85):</span> Very strict. The match must be almost identical in wording.
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-300">Lower (e.g., 0.65):</span> More flexible. Accepts concepts that mean roughly the same thing.
                  </p>
                </div>

                <div className="border-t border-gray-600 my-3"></div>

                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-300 mb-1">Example Scenario:</p>
                    <div className="bg-gray-700 p-2 rounded text-xs space-y-1">
                      <p><span className="text-gray-400">Reference:</span> "I am completely worthless"</p>
                      <p><span className="text-gray-400">Doctor found:</span> "I feel inadequate"</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-900 p-2 rounded border border-gray-600">
                      <p className="font-bold text-green-300 mb-1">High (0.85)</p>
                      <p className="text-red-400 font-bold">❌ Miss</p>
                      <p className="text-gray-500 mt-1">Too different.</p>
                    </div>
                    <div className="bg-gray-900 p-2 rounded border border-gray-600">
                      <p className="font-bold text-yellow-300 mb-1">Low (0.65)</p>
                      <p className="text-green-400 font-bold">✅ Match</p>
                      <p className="text-gray-500 mt-1">Close enough.</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 border-4 border-transparent border-b-gray-800"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(settings.similarity_thresholds).map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm md:text-base lg:text-lg text-gray-600 mb-2">
                  {componentNames.similarity_thresholds[key] || key.replace(/_/g, " ")}
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={value}
                  onChange={(e) => updateSetting(therapyType, `similarity_thresholds.${key}`, parseFloat(e.target.value))}
                  disabled={!isAdmin}
                  className="w-full px-3 py-3 text-base md:text-lg border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm md:text-base lg:text-lg text-gray-500">
            Higher values = stricter matching. Adjust based on therapy-specific component importance.
          </p>
        </div> */}

        {/* Component Weights */}
        {/* <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <label className="text-base md:text-lg font-bold text-gray-700">
              Component Weights (Must sum to 1.0)
            </label>
            <div className="relative group">
              <Info className="w-5 h-5 text-gray-400 cursor-help hover:text-gray-600" />
              <div className="absolute z-10 w-96 p-4 mt-2 text-sm text-white bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 z-50">
                <p className="font-bold mb-3 text-blue-300">Components Impact on Score</p>

                <div className="space-y-2 mb-4">
                  <p>
                    These weights determine how much each component contributes to the final session percentage.
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-xs text-gray-300">
                    <li><span className="text-green-300 font-semibold">Important tasks</span> (e.g., Core Beliefs) need higher weights.</li>
                    <li><span className="text-yellow-300 font-semibold">Minor details</span> can have lower weights.</li>
                  </ul>
                  <p className="font-bold text-red-300 text-xs mt-2">Total must sum to exactly 1.0.</p>
                </div>

                <div className="border-t border-gray-600 my-3"></div>

                <div className="space-y-3">
                  <p className="font-semibold text-gray-300 mb-1">Example: Scoring 100% on a task</p>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-900 p-2 rounded border border-gray-600">
                      <p className="font-bold text-green-300 mb-1">High Weight (0.40)</p>
                      <p className="text-white">+40% to Final Grade</p>
                      <p className="text-gray-500 mt-1">Big impact.</p>
                    </div>
                    <div className="bg-gray-900 p-2 rounded border border-gray-600">
                      <p className="font-bold text-yellow-300 mb-1">Low Weight (0.10)</p>
                      <p className="text-white">+10% to Final Grade</p>
                      <p className="text-gray-500 mt-1">Small impact.</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 border-4 border-transparent border-b-gray-800"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(settings.component_weights).map(([key, value]) => {
              const total = Object.values(settings.component_weights).reduce(
                (sum, val) => sum + parseFloat(val),
                0
              );
              const isValid = Math.abs(total - 1.0) < 0.01;

              return (
                <div key={key}>
                  <label className="block text-sm md:text-base lg:text-lg text-gray-600 mb-2">
                    {componentNames.component_weights[key] || key.replace(/_/g, " ")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={value}
                    onChange={(e) => updateSetting(therapyType, `component_weights.${key}`, parseFloat(e.target.value))}
                    disabled={!isAdmin}
                    className={`w-full px-3 py-3 text-base md:text-lg border rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${!isValid ? "border-red-300" : "border-gray-300"
                      }`}
                  />
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-sm md:text-base lg:text-lg text-gray-500">
            Total: {Object.values(settings.component_weights).reduce((sum, val) => sum + parseFloat(val), 0).toFixed(2)}
            {Math.abs(Object.values(settings.component_weights).reduce((sum, val) => sum + parseFloat(val), 0) - 1.0) > 0.01 && (
              <span className="text-red-600 ml-2">⚠️ Must equal 1.0</span>
            )}
          </p>
        </div> */}

        {/* <div className="mb-6">
          <label className="flex items-start sm:items-center">
            <input
              type="checkbox"
              checked={settings.use_semantic}
              onChange={(e) => updateSetting(therapyType, "use_semantic", e.target.checked)}
              disabled={!isAdmin}
              className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:bg-gray-100 mt-1 sm:mt-0 flex-shrink-0"
              data-label="Enable Semantic Similarity"
            />
            <span className="text-base md:text-lg font-bold text-gray-700">
              Enable Semantic Similarity Matching
            </span>
          </label>
          <p className="mt-2 text-sm md:text-base lg:text-lg text-gray-500 ml-8">
            Uses AI embeddings to match synonyms and paraphrases (e.g., "I'm not good enough" vs "I'm inadequate").
          </p>
        </div> */}

        {/* Phase Specific Scoring Details */}
        <div className="mt-12 pt-8 border-t-2 border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-secondary-800 font-heading">Phase Specific Scoring</h2>
              <p className="text-sm text-secondary-500">Define mandatory competencies and weights per phase, grouped by clinical domains.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
              <label className="text-base font-bold text-secondary-800 tracking-wider">Select Phase to Configure:</label>
              <select
                value={selectedPhase}
                onChange={(e) => setSelectedPhase(e.target.value)}
                className="flex-1 max-w-xs px-4 py-2 bg-white border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {availablePhases[therapyType]?.map(phase => (
                  <option key={phase} value={phase}>{phase}</option>
                ))}
              </select>
            </div>

            {selectedPhase && phaseMetadata[therapyType]?.phases?.[selectedPhase] && (
              <div className="space-y-8">
                {Object.entries(phaseMetadata[therapyType].phases[selectedPhase].domains).map(([domainKey, domain]) => {
                  // Calculate subtotal for the domain
                  const competencies = Array.isArray(domain) ? domain : (domain.competencies || []);
                  const domainName = domain.name || domainKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  const domainSubtotal = competencies.reduce((sum, comp) => {
                    const therapyPhaseSettings = phaseSettings[therapyType]?.[selectedPhase] || { mandatory: [], weights: {} };
                    const curWeightsMap = therapyPhaseSettings.weights || therapyPhaseSettings.competency_weights || {};
                    return sum + parseFloat(curWeightsMap[comp.key] ?? 0);
                  }, 0);

                  return (
                    <div key={domainKey} className="mb-12">
                      <div className="flex justify-between items-end mb-2 px-2">
                        <h3 className="text-xl font-bold text-secondary-800 font-heading">{domainName}</h3>
                        <div className="text-xl font-bold text-primary-600">
                          {domainSubtotal.toFixed(2)}
                        </div>
                      </div>
                      <div className="border-b-[1.5px] border-primary-50/80 mb-6"></div>

                      <div className="space-y-4">
                        {competencies.map(comp => {
                          const therapyPhaseSettings = phaseSettings[therapyType]?.[selectedPhase] || { mandatory: [], weights: {} };
                          // Fallback to metadata initial values if not in current settings state
                          const curMandatoryList = therapyPhaseSettings.mandatory || therapyPhaseSettings.important || [];
                          const curWeightsMap = therapyPhaseSettings.weights || therapyPhaseSettings.competency_weights || {};

                          const isMandatory = curMandatoryList.includes(comp.key) || comp.is_locked;
                          const weight = curWeightsMap[comp.key] ?? 0;

                          return (
                            <div key={comp.key} className="bg-white rounded-xl border border-secondary-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                              <div className="mb-4">
                                <div className="text-lg font-bold text-secondary-800 font-heading">{comp.name}:</div>
                                <div className="text-secondary-500 text-sm mt-1 leading-relaxed">{comp.description}</div>
                              </div>

                              <div className="flex items-center gap-8 pt-2">
                                <label className={`flex items-center gap-3 ${comp.is_locked ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}>
                                  <input
                                    type="checkbox"
                                    checked={isMandatory}
                                    onChange={(e) => updatePhaseSetting(therapyType, selectedPhase, "mandatory", { competency: comp.key, checked: e.target.checked })}
                                    disabled={!isAdmin || comp.is_locked}
                                    className={`w-5 h-5 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 ${comp.is_locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                  />
                                  <span className="text-base font-medium text-secondary-700 select-none">
                                    Mandatory {comp.is_locked && <span className="text-[10px] bg-primary-50 text-primary-600 px-1.5 py-0.5 rounded ml-1 font-bold uppercase tracking-wider">(Locked)</span>}
                                  </span>
                                </label>

                                <div className="flex items-center gap-3">
                                  <label className="text-base font-medium text-secondary-700">Score:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={weight}
                                    onChange={(e) => updatePhaseSetting(therapyType, selectedPhase, `weights.${comp.key}`, parseFloat(e.target.value))}
                                    disabled={!isAdmin || !isMandatory}
                                    className={`w-24 px-3 py-2 text-center text-lg font-medium border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-secondary-50 transition-all ${!isMandatory ? 'opacity-50 grayscale cursor-not-allowed' : 'hover:bg-white focus:bg-white'
                                      }`}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Grand Total Footer */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 border-t border-gray-200 pt-8 mt-12">
                  <div className="flex items-center gap-4 ml-auto">
                    <span className="text-lg font-bold text-secondary-800">Total:</span>
                    <span className={`text-xl font-bold ${Math.abs(Object.values(phaseSettings[therapyType]?.[selectedPhase]?.weights || phaseSettings[therapyType]?.[selectedPhase]?.competency_weights || {}).reduce((s, v) => s + parseFloat(v || 0), 0) - 1.0) < 0.01
                      ? "text-green-600" : "text-red-500"
                      }`}>
                      {Object.values(phaseSettings[therapyType]?.[selectedPhase]?.weights || phaseSettings[therapyType]?.[selectedPhase]?.competency_weights || {}).reduce((s, v) => s + parseFloat(v || 0), 0).toFixed(2)}
                    </span>
                    {Math.abs(Object.values(phaseSettings[therapyType]?.[selectedPhase]?.competency_weights || phaseSettings[therapyType]?.[selectedPhase]?.weights || {}).reduce((s, v) => s + parseFloat(v || 0), 0) - 1.0) < 0.01 ? (
                      <span className="text-xl">✅</span>
                    ) : (
                      <span className="text-xs text-red-500 font-bold ml-2">(Must sum to 1.00)</span>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center mt-8">
                  <button
                    onClick={handleResetPhaseScoring}
                    disabled={!isAdmin || saving}
                    className="px-5 py-2.5 text-red-600 bg-red-50 border border-red-200 font-medium rounded-md hover:bg-red-100 hover:border-red-300 disabled:opacity-50 transition-all flex items-center gap-2"
                    title="Remove all custom weights and revert to the system's built-in defaults"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to System Defaults
                  </button>
                  <button
                    onClick={() => handleSavePhaseConfig(therapyType)}
                    disabled={!isAdmin || saving}
                    className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {saving ? "Saving..." : <><Save className="w-5 h-5" /> Save {selectedPhase} Rules</>}
                  </button>
                </div>
              </div>
            )}

            {selectedPhase && !phaseMetadata[therapyType]?.phases?.[selectedPhase] && (
              <div className="py-12 mt-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">No structure metadata available for this phase.</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t">
          <button
            onClick={() => handleReset(therapyType)}
            disabled={!isAdmin}
            className="w-full sm:w-auto px-6 py-3 text-base md:text-lg font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-label={`Reset ${therapyType} Defaults`}
          >
            Reset to Defaults
          </button>
          <button
            onClick={() => handleSave(therapyType)}
            disabled={!isAdmin || saving}
            className="w-full sm:w-auto px-8 py-3 text-base md:text-lg font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm cursor-pointer"
            data-label={`Save ${therapyType} Settings`}
          >
            {saving ? "Saving..." : `Save ${therapyType} Settings`}
          </button>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-full md:max-w-4xl lg:max-w-6xl mx-auto py-4 md:py-6 px-4 md:px-6">
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8 lg:p-10 flex items-center justify-center">
            <p className="text-sm md:text-base text-gray-600">Loading settings...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h1 className="text-3xl md:text-4xl lg:text-4xl font-bold text-gray-600">Organization Settings</h1>
              {!isAdmin && (
                <span className="text-base md:text-lg text-gray-500 bg-yellow-50 px-3 md:px-4 py-1 md:py-2 rounded self-start sm:self-auto">
                  {isBranchAdmin ? "Read-only, except your own Branch" : "Read-only (Admin only)"}
                </span>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              {/* Left sub navigation */}
              <div className="w-full md:w-64 flex-shrink-0">
                <nav className="space-y-2 sticky top-6">
                  <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden p-2">
                    <button
                      type="button"
                      onClick={() => setActiveSection("General")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "General"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <SettingsIcon className={`w-5 h-5 ${activeSection === "General" ? "text-white" : "text-secondary-400"}`} />
                        <div>
                          <div>Appointment Fee</div>
                          <div className={`text-[11px] ${activeSection === "General" ? "text-primary-100" : "text-secondary-400"}`}>Policies & Checkout Fees</div>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveSection("Prescription")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "Prescription"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className={`w-5 h-5 ${activeSection === "Prescription" ? "text-white" : "text-secondary-400"}`} />
                        <div>
                          <div>Address and Prescription</div>
                          <div className={`text-[11px] ${activeSection === "Prescription" ? "text-primary-100" : "text-secondary-400"}`}>Clinic Address, Coordinates & Header</div>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveSection("Branches")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "Branches"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${activeSection === "Branches" ? "text-white" : "text-secondary-400"}`} />
                        <div>
                          <div>Branches</div>
                          <div className={`text-[11px] ${activeSection === "Branches" ? "text-primary-100" : "text-secondary-400"}`}>Locations, Address & Letterhead</div>
                        </div>
                      </div>
                    </button>
                    {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setActiveSection("PatientAlerts")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "PatientAlerts"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <BellRing className={`w-5 h-5 ${activeSection === "PatientAlerts" ? "text-white" : "text-secondary-400"}`} />
                        <div>
                          <div>Patient alerts</div>
                          <div className={`text-[11px] ${activeSection === "PatientAlerts" ? "text-primary-100" : "text-secondary-400"}`}>App, SMS, WhatsApp & email</div>
                        </div>
                      </div>
                    </button>
                    )}
                    {hasClinicalSettings(features) && (
                    <button
                      type="button"
                      onClick={() => setActiveSection("Clinical")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "Clinical"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <ShieldAlert className={`w-5 h-5 ${activeSection === "Clinical" ? "text-white" : "text-secondary-400"}`} />
                        <div>
                          <div>Clinical & Safety</div>
                          <div className={`text-[11px] ${activeSection === "Clinical" ? "text-primary-100" : "text-secondary-400"}`}>Notes privacy & risk assessment</div>
                        </div>
                      </div>
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveSection("FrameworkPrivacy")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "FrameworkPrivacy"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <Shield className={`w-5 h-5 ${activeSection === "FrameworkPrivacy" ? "text-white" : "text-secondary-400"}`} />
                        <span>Framework & Privacy</span>
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${activeSection === "FrameworkPrivacy" ? "bg-white/20 text-white" : "bg-purple-100 text-purple-700"}`}>
                        Psychology
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveSection("Scoring")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "Scoring"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <Scale className={`w-5 h-5 ${activeSection === "Scoring" ? "text-white" : "text-secondary-400"}`} />
                        <span>Scoring Models</span>
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${activeSection === "Scoring" ? "bg-white/20 text-white" : "bg-purple-100 text-purple-700"}`}>
                        Psychology
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveSection("Experience")}
                      className={`w-full flex items-center justify-between text-left px-4 py-3 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${activeSection === "Experience"
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/25"
                        : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <TrendingUp className={`w-5 h-5 ${activeSection === "Experience" ? "text-white" : "text-secondary-400"}`} />
                        <span>Experience & Complexity</span>
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${activeSection === "Experience" ? "bg-white/20 text-white" : "bg-purple-100 text-purple-700"}`}>
                        Psychology
                      </span>
                    </button>
                  </div>
                </nav>
              </div>

              {/* Right content area */}
              <div className="flex-1">
                {activeSection === "Scoring" && (
                  <>
                    <div className="mb-4 flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-purple-200 text-purple-800 rounded">
                          Psychology Only
                        </span>
                        <span className="text-xs md:text-sm text-purple-900 font-medium">
                          These models (CBT, REBT, SFBT) are dedicated strictly to psychotherapy training and case simulation.
                        </span>
                      </div>
                    </div>

                    {/* Therapy Type Tabs */}
                    <div className="mb-6 border-b border-secondary-200">
                      <nav className="flex space-x-4" aria-label="Tabs">
                        {["CBT", "REBT", "SFBT"].map((therapyType) => (
                          <button
                            key={therapyType}
                            onClick={() => setActiveTab(therapyType)}
                            data-label={`${therapyType} Tab`}
                            className={`py-4 px-6 text-base md:text-lg font-medium border-b-2 transition-colors ${activeTab === therapyType
                              ? "border-primary-600 text-primary-600 font-bold"
                              : "border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300"
                              }`}
                          >
                            {therapyType}
                            {therapyType === "CBT" && " (Cognitive Behavioral Therapy)"}
                            {therapyType === "REBT" && " (Rational Emotive Behavior Therapy)"}
                            {therapyType === "SFBT" && " (Solution-Focused Brief Therapy)"}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* Therapy Description */}
                    <div className="mb-6 p-4 bg-primary-50/70 border border-primary-100 rounded-lg">
                      <h2 className="text-lg md:text-xl font-semibold text-primary-800 mb-2">
                        {activeTab === "CBT" && "Cognitive Behavioral Therapy (CBT)"}
                        {activeTab === "REBT" && "Rational Emotive Behavior Therapy (REBT)"}
                        {activeTab === "SFBT" && "Solution-Focused Brief Therapy (SFBT)"}
                      </h2>
                      <p className="text-sm md:text-base text-primary-600">
                        {activeTab === "CBT" &&
                          "Configure scoring parameters for CBT cognitive model comparison. Adjust similarity thresholds and component weights to match your organization's evaluation standards."}
                        {activeTab === "REBT" &&
                          "Configure scoring parameters for REBT model comparison. REBT focuses on irrational beliefs, activating events, and consequences using the ABC model."}
                        {activeTab === "SFBT" &&
                          "Configure scoring parameters for SFBT model comparison. SFBT emphasizes exceptions, strengths, scaling progress, and preferred future goals."}
                      </p>
                    </div>

                    {/* Render Active Therapy Settings */}
                    {renderTherapySettings(activeTab)}

                  </>
                )}

                {activeSection === "General" && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-1">
                        Appointment Fee Settings
                      </h2>
                      <p className="text-sm md:text-base text-gray-500">
                        Configure clinic-wide appointment policies, payment options, and convenience fees applicable across all departments.
                      </p>
                    </div>

                    <PayoutBankDetailsCard />

                    <ClinicPageCard />

                    {/* Appointment & Payment Policies */}
                    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
                          <SettingsIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-semibold text-gray-800">
                            Appointment & Payment Policies
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            Configure booking payment rules, Pay at Clinic options, and checkout convenience fees.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-6 pt-2">
                        {/* Max Advance Booking Window */}
                        <div>
                          <label className="block text-sm md:text-base font-semibold text-gray-800 mb-1">
                            Maximum Advance Booking Window
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={maxAdvanceBookingDays}
                              onChange={(e) => setMaxAdvanceBookingDays(e.target.value)}
                              onBlur={(e) => setMaxAdvanceBookingDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                              disabled={!isAdmin}
                              className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">days ahead</span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            Patients can't select a date further out than this - without a limit, nothing stops booking a year in advance. Applies to all doctors in this organization.
                          </p>
                        </div>

                        {/* Open Pay at Clinic bookings per phone */}
                        <div>
                          <label className="block text-sm md:text-base font-semibold text-gray-800 mb-1">
                            Open Pay at Clinic Bookings per Phone
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={maxOpenUnpaidBookings}
                              onChange={(e) => setMaxOpenUnpaidBookings(e.target.value)}
                              onBlur={(e) => setMaxOpenUnpaidBookings(Math.max(1, parseInt(e.target.value, 10) || 1))}
                              disabled={!isAdmin}
                              className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">upcoming bookings</span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            How many upcoming unpaid (Pay at Clinic) appointments one phone number can hold at once in this organization. Stops someone blocking doctors' slots for free. Paid bookings and staff-created appointments don't count, and a patient can still book only one unpaid appointment per doctor at a time.
                          </p>
                        </div>

                        {/* Cancellation policy (feature "attendance") */}
                        {features.has("attendance") && (
                          <div className="md:col-span-2 rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                            <p className="text-sm md:text-base font-semibold text-gray-800">Cancellation Policy</p>
                            <textarea value={cancellationPolicy} onChange={(e) => setCancellationPolicy(e.target.value)} disabled={!isAdmin} rows={3} maxLength={2000}
                              aria-label="Cancellation policy shown to patients"
                              placeholder="e.g. Please cancel at least 24 hours before your appointment. Later cancellations are charged 50% of the fee."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
                            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                              <span>A patient cancelling within</span>
                              <input type="number" min="0" max="168" value={lateCancelWindow} disabled={!isAdmin} onWheel={(e) => e.currentTarget.blur()}
                                onChange={(e) => setLateCancelWindow(e.target.value)} aria-label="Late cancellation window in hours"
                                className="w-20 px-2 py-1.5 border border-gray-300 rounded-md" />
                              <span>hours of the start pays</span>
                              <input type="number" min="0" value={lateFeeValue} disabled={!isAdmin} onWheel={(e) => e.currentTarget.blur()}
                                onChange={(e) => setLateFeeValue(e.target.value)} aria-label="Late cancellation fee"
                                className="w-24 px-2 py-1.5 border border-gray-300 rounded-md" />
                              <select value={lateFeeType} disabled={!isAdmin} onChange={(e) => setLateFeeType(e.target.value)} aria-label="Fee type"
                                className="px-2 py-1.5 border border-gray-300 rounded-md bg-white">
                                <option value="percent">% of the fee</option>
                                <option value="flat">₹ (flat)</option>
                              </select>
                            </div>
                            <p className="text-xs md:text-sm text-gray-500">
                              Patients see the policy when booking and before cancelling in the patient app. Online payments are refunded minus the fee; for Pay at Clinic the fee is recorded on the appointment as owed. 0 = no fee. Cancellations by the clinic are never charged.
                            </p>
                          </div>
                        )}

                        {/* Unpaid online visits (a series accepted in one go is paid per visit) */}
                        <div>
                          <label htmlFor="unpaid_release" className="block text-sm md:text-base font-semibold text-gray-800 mb-1">
                            Cancel Unpaid Online Visits
                          </label>
                          <select id="unpaid_release" value={unpaidReleaseHours} disabled={!isAdmin}
                            onChange={(e) => setUnpaidReleaseHours(parseInt(e.target.value, 10))}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500">
                            <option value={-1}>Never - keep the visit, the patient just can't join until paid</option>
                            <option value={0}>At the start time</option>
                            {[2, 6, 12, 24].map((h) => <option key={h} value={h}>{h} hours before the visit</option>)}
                          </select>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            When a patient accepts a repeating series at once, each online visit is paid separately. A visit still unpaid at this point is cancelled and the slot freed; the patient and the doctor are told. The patient gets a reminder in the 24 hours before the deadline.
                          </p>
                        </div>

                        {/* Reschedule Cutoff */}
                        <div>
                          <label className="block text-sm md:text-base font-semibold text-gray-800 mb-1">
                            Reschedule Cutoff Window
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="720"
                              value={rescheduleCutoffHours}
                              onChange={(e) => setRescheduleCutoffHours(e.target.value)}
                              onBlur={(e) => setRescheduleCutoffHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
                              disabled={!isAdmin}
                              className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">hours before the appointment</span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            A patient can no longer reschedule their own appointment through the app once it's this close - they'd need to contact the clinic directly. Set to 0 to allow rescheduling right up until the appointment. Staff rescheduling from this app are never affected by this limit.
                          </p>
                        </div>

                        {/* Same-day changes by the patient */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={allowSameDayChanges}
                            onChange={(e) => setAllowSameDayChanges(e.target.checked)}
                            disabled={!isAdmin}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            id="same-day-changes-toggle"
                          />
                          <div>
                            <label htmlFor="same-day-changes-toggle" className="text-sm md:text-base font-semibold text-gray-800 cursor-pointer">
                              Let Patients Change Same-Day Visits
                            </label>
                            <div className="text-xs md:text-sm text-gray-500 mt-1">
                              Off (default): on the day of the visit, patients can't cancel or reschedule it in the app and are asked to contact the clinic. Turn on to allow it, still subject to the cutoff window above. Staff can always change visits.
                            </div>
                          </div>
                        </div>

                        {/* Follow-Up Reminder Advance Notice */}
                        <div>
                          <label className="block text-sm md:text-base font-semibold text-gray-800 mb-1">
                            Follow-Up Reminder Lead Time
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="60"
                              value={followUpAdvanceNoticeDays}
                              onChange={(e) => setFollowUpAdvanceNoticeDays(e.target.value)}
                              onBlur={(e) => setFollowUpAdvanceNoticeDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                              disabled={!isAdmin}
                              className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">days before</span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            How early a "Follow-Up Due" reminder (set by a doctor from a consultation's Plan tab, to re-engage a patient) should start showing up for staff. Showing it only on the day itself leaves no time to actually reach the patient and get them booked in - default is 3 days.
                          </p>
                        </div>

                        {/* Allow Unpaid Booking */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={allowUnpaidBooking}
                            onChange={(e) => setAllowUnpaidBooking(e.target.checked)}
                            disabled={!isAdmin}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            id="unpaid-booking-toggle"
                          />
                          <div>
                            <label htmlFor="unpaid-booking-toggle" className="text-sm md:text-base font-semibold text-gray-800 cursor-pointer">
                              Allow Booking Without Payment (Pay at Clinic)
                            </label>
                            <div className="text-xs md:text-sm text-gray-500 mt-1">
                              When enabled, patients can choose between paying online (Prepaid) or paying at clinic during their appointment visit. If disabled, online payment is mandatory to confirm booking.
                            </div>
                          </div>
                        </div>

                        {allowUnpaidBooking && (
                          <div className="mt-3 pl-7 space-y-3 pt-3 border-t border-gray-200">
                            <label className="block text-sm font-semibold text-gray-700">
                              Pay at Clinic Fee Adjustment (Applied to all doctors)
                            </label>
                            <div className="flex flex-wrap items-center gap-3">
                              <select
                                value={unpaidFeeDifferenceType}
                                onChange={(e) => setUnpaidFeeDifferenceType(e.target.value)}
                                disabled={!isAdmin}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                              >
                                <option value="flat">Flat Amount (₹)</option>
                                <option value="percent">Percentage (%)</option>
                              </select>
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 py-2">
                                  {unpaidFeeDifferenceType === "percent" ? "%" : "₹"}
                                </span>
                                <input
                                  type="number"
                                  value={unpaidFeeDifferenceValue}
                                  onChange={(e) => setUnpaidFeeDifferenceValue(e.target.value)}
                                  onBlur={(e) => setUnpaidFeeDifferenceValue(parseFloat(e.target.value) || 0)}
                                  disabled={!isAdmin}
                                  className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              Example: If doctor base fee is ₹1,000, setting {unpaidFeeDifferenceType === "percent" ? "+10%" : "+₹50"} charges {unpaidFeeDifferenceType === "percent" ? "₹1,100" : "₹1,050"} for Pay at Clinic. Set to 0 for identical fees.
                            </p>
                          </div>
                        )}

                        {/* Convenience & Platform Fee Configuration */}
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={platformFeeEnabled}
                              onChange={(e) => setPlatformFeeEnabled(e.target.checked)}
                              disabled={!isAdmin}
                              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              id="platform-fee-toggle"
                            />
                            <div className="flex-1">
                              <label htmlFor="platform-fee-toggle" className="text-sm md:text-base font-semibold text-gray-800 cursor-pointer">
                                Convenience & Platform Fee (Online Checkout)
                              </label>
                              <div className="text-xs md:text-sm text-gray-500 mt-1">
                                Control whether a convenience/platform fee is displayed and charged to patients on the booking review and payment screen.
                              </div>

                              {platformFeeEnabled && (
                                <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg space-y-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                                    <div>
                                      <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
                                        Platform Fee Amount (₹)
                                      </label>
                                      <div className="flex items-center">
                                        <span className="text-sm font-medium text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 py-2">
                                          ₹
                                        </span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="1"
                                          value={platformFeeAmount}
                                          onChange={(e) => setPlatformFeeAmount(e.target.value)}
                                          onBlur={(e) => setPlatformFeeAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                          disabled={!isAdmin}
                                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500"
                                          placeholder="49"
                                        />
                                      </div>
                                    </div>

                                    <div className="pt-4 sm:pt-0">
                                      <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={platformFeeWaived}
                                          onChange={(e) => setPlatformFeeWaived(e.target.checked)}
                                          disabled={!isAdmin}
                                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                        />
                                        <div>
                                          <span className="text-sm font-semibold text-gray-800">
                                            Waive Fee (Promotional Discount)
                                          </span>
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            Displays as <span className="line-through text-gray-400">₹{platformFeeAmount}</span> <span className="text-emerald-600 font-bold">FREE</span> for patients.
                                          </p>
                                        </div>
                                      </label>
                                    </div>
                                  </div>

                                  {/* Live Preview */}
                                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-between text-xs">
                                    <span className="text-emerald-900 font-medium">
                                      Patient Checkout Preview:
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600">Convenience & Platform Fee:</span>
                                      {platformFeeWaived ? (
                                        <div className="flex items-center gap-1.5 font-bold">
                                          <span className="line-through text-gray-400">₹{platformFeeAmount}</span>
                                          <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">FREE (Waived)</span>
                                        </div>
                                      ) : (
                                        <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                                          ₹{platformFeeAmount} (Added to Total)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Online Payment Collection */}
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={onlinePaymentEnabled}
                              onChange={(e) => setOnlinePaymentEnabled(e.target.checked)}
                              disabled={!isAdmin}
                              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              id="online-payment-toggle"
                            />
                            <div className="flex-1">
                              <label htmlFor="online-payment-toggle" className="text-sm md:text-base font-semibold text-gray-800 cursor-pointer">
                                Allow Online Payment Collection
                              </label>
                              <div className="text-xs md:text-sm text-gray-500 mt-1">
                                When disabled, patients can no longer pay online or book Video Consultations (which require online payment) - only Pay at Clinic, in-person bookings remain available. Turning this off while "Allow Booking Without Payment" above is also off leaves patients with no way to book at all.
                              </div>

                              {onlinePaymentEnabled && (
                                <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
                                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    Accepted Payment Methods at Checkout
                                  </label>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                      { key: 'card', label: 'Cards (Visa, Mastercard, Diners...)' },
                                      { key: 'upi', label: 'UPI' },
                                      { key: 'netbanking', label: 'Netbanking' },
                                      { key: 'wallet', label: 'Wallets' },
                                      { key: 'emi', label: 'EMI' },
                                      { key: 'paylater', label: 'Pay Later' },
                                    ].map((m) => (
                                      <label key={m.key} className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={!!allowedPaymentMethods[m.key]}
                                          onChange={(e) => setAllowedPaymentMethods(prev => ({ ...prev, [m.key]: e.target.checked }))}
                                          disabled={!isAdmin}
                                          className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-700">{m.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                  <p className="text-[11px] text-gray-400 mt-3">
                                    Applies only at checkout (one shared Razorpay account) - individual card networks like Diners can't be excluded separately from Cards as a whole.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                      </div>

                      {isAdmin && (
                        <div className="mt-6 flex justify-end pt-4 border-t border-secondary-200">
                          <button
                            type="button"
                            onClick={handleSaveGeneral}
                            disabled={saving}
                            className="px-6 py-2.5 text-sm md:text-base font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-colors cursor-pointer"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? "Saving..." : "Save General Settings"}
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                )}

                {activeSection === "Branches" && (
                  <div className="space-y-6">
                    <div className="pb-2 border-b border-secondary-200">
                      <h2 className="text-lg md:text-xl font-bold text-secondary-900">Branches</h2>
                      <p className="text-sm md:text-base text-secondary-500">
                        Manage your organization's physical locations. Each branch can have its own address, contact
                        details, and prescription letterhead. Doctors assign a branch to each shift in their weekly
                        schedule, so a doctor working across multiple locations shows the right address to patients
                        booking each slot.
                      </p>
                    </div>

                    {(isAdmin || isBranchAdmin) && (
                      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-4">
                        <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                          <Building2 className="w-5 h-5 text-primary-600" />
                          <h3 className="text-base font-bold text-secondary-900">
                            {isBranchAdmin && !isAdmin ? "Your Branch" : editingBranchId ? "Edit Branch" : "Add Branch"}
                          </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Branch Name</label>
                            <input
                              type="text"
                              value={branchForm.name}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, name: e.target.value }))}
                              placeholder="e.g. Andheri Branch"
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Phone Number</label>
                            <input
                              type="text"
                              value={branchForm.phone_number}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, phone_number: e.target.value }))}
                              placeholder="+91 98765 43210"
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">State</label>
                            <select
                              value={branchForm.state_id}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, state_id: e.target.value, city_id: "" }))}
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            >
                              <option value="">Select State</option>
                              {branchLocations.states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">City</label>
                            <select
                              value={branchForm.city_id}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, city_id: e.target.value }))}
                              disabled={!branchForm.state_id}
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900 disabled:bg-secondary-100"
                            >
                              <option value="">Select City</option>
                              {availableBranchCities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Address</label>
                            <textarea
                              value={branchForm.address}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, address: e.target.value }))}
                              rows={2}
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Latitude</label>
                            <input
                              type="text"
                              value={branchForm.latitude}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, latitude: e.target.value }))}
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Longitude</label>
                            <input
                              type="text"
                              value={branchForm.longitude}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, longitude: e.target.value }))}
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                            />
                          </div>
                        </div>

                        <div className="border-t border-secondary-100 pt-4">
                          <div className="text-xs font-bold uppercase tracking-wider text-secondary-500 mb-3">
                            Prescription Letterhead Overrides <span className="normal-case font-normal text-secondary-400">(optional - falls back to your organization's own letterhead when blank)</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Hospital / Clinic Name</label>
                              <input
                                type="text"
                                value={branchForm.hospital_name}
                                onChange={(e) => setBranchForm((prev) => ({ ...prev, hospital_name: e.target.value }))}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Slogan</label>
                              <input
                                type="text"
                                value={branchForm.slogan}
                                onChange={(e) => setBranchForm((prev) => ({ ...prev, slogan: e.target.value }))}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Email</label>
                              <input
                                type="text"
                                value={branchForm.email}
                                onChange={(e) => setBranchForm((prev) => ({ ...prev, email: e.target.value }))}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Website</label>
                              <input
                                type="text"
                                value={branchForm.website}
                                onChange={(e) => setBranchForm((prev) => ({ ...prev, website: e.target.value }))}
                                className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-3">
                          <label className="flex items-center gap-2 text-sm font-semibold text-secondary-800">
                            <input
                              type="checkbox"
                              checked={branchForm.promo_active}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, promo_active: e.target.checked }))}
                              className="rounded border-secondary-300 text-amber-600 focus:ring-amber-500"
                            />
                            Run a promotion at this branch
                          </label>
                          <p className="text-xs text-secondary-500">
                            Discounts the doctor's consultation fee for patients booking at this branch specifically. Patients see the original price struck through next to the discounted price - it's never a silent price change.
                          </p>
                          {branchForm.promo_active && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                              <div className="sm:col-span-2">
                                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Promo Label (shown to patients)</label>
                                <input
                                  type="text"
                                  value={branchForm.promo_label}
                                  onChange={(e) => setBranchForm((prev) => ({ ...prev, promo_label: e.target.value }))}
                                  placeholder="e.g. New Branch Launch Offer"
                                  className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm text-secondary-900"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Discount</label>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={branchForm.promo_discount_type}
                                    onChange={(e) => setBranchForm((prev) => ({ ...prev, promo_discount_type: e.target.value }))}
                                    className="px-3 py-2.5 text-sm border border-secondary-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                                  >
                                    <option value="percent">%</option>
                                    <option value="flat">₹ flat</option>
                                  </select>
                                  <input
                                    type="number"
                                    min="0"
                                    value={branchForm.promo_discount_value}
                                    onChange={(e) => setBranchForm((prev) => ({ ...prev, promo_discount_value: parseFloat(e.target.value) || 0 }))}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm text-secondary-900"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Valid Until (optional)</label>
                                <input
                                  type="date"
                                  value={branchForm.promo_valid_until || ""}
                                  onChange={(e) => setBranchForm((prev) => ({ ...prev, promo_valid_until: e.target.value }))}
                                  className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm text-secondary-900"
                                />
                                <p className="text-[11px] text-secondary-400 mt-1">Leave blank to run indefinitely until turned off.</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {!isBranchAdmin || isAdmin ? (
                          <label className="flex items-center gap-2 text-sm text-secondary-700">
                            <input
                              type="checkbox"
                              checked={branchForm.is_default}
                              onChange={(e) => setBranchForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                              className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                            />
                            Set as default branch (used when a shift has no branch assigned)
                          </label>
                        ) : null}

                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleSaveBranch}
                            disabled={savingBranch}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white text-sm font-semibold shadow-sm transition-colors"
                          >
                            {editingBranchId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                            {savingBranch ? "Saving..." : editingBranchId ? "Update Branch" : "Add Branch"}
                          </button>
                          {editingBranchId && isAdmin && (
                            <button
                              type="button"
                              onClick={handleCancelBranchEdit}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-secondary-600 hover:bg-secondary-50 text-sm font-semibold transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {(!isBranchAdmin || isAdmin) && (
                    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-3">
                      <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                        <Building2 className="w-5 h-5 text-primary-600" />
                        <h3 className="text-base font-bold text-secondary-900">All Branches</h3>
                      </div>

                      {branchesLoading ? (
                        <p className="text-sm text-secondary-400 py-4">Loading branches...</p>
                      ) : branches.length === 0 ? (
                        <p className="text-sm text-secondary-400 py-4">No branches yet. Add one above to get started.</p>
                      ) : (
                        <div className="divide-y divide-secondary-100">
                          {branches.map((b) => (
                            <div key={b.id} className="flex items-center justify-between py-3 gap-3">
                              <div>
                                <div className="text-sm font-semibold text-secondary-900 flex items-center gap-2">
                                  {b.name}
                                  {b.is_default && (
                                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-200">
                                      Default
                                    </span>
                                  )}
                                  {b.promo_active && (
                                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                      🎉 Promo Active
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-secondary-500">{b.address || "No address set"}</div>
                                {b.phone_number && <div className="text-xs text-secondary-400">{b.phone_number}</div>}
                              </div>
                              {isAdmin && (
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleEditBranch(b)}
                                    className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                    title="Edit branch"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteBranch(b.id)}
                                    className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete branch"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}

                {activeSection === "Prescription" && (
                  <div className="space-y-6">
                    <div className="pb-2 border-b border-secondary-200">
                      <div>
                        <div className="flex items-center gap-2.5 mb-1">
                          <h2 className="text-lg md:text-xl font-bold text-secondary-900">
                            Address and Prescription Settings
                          </h2>
                          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-primary-50 text-primary-700 rounded border border-primary-200">
                            All Clinics & Medicine
                          </span>
                        </div>
                        <p className="text-sm md:text-base text-secondary-500">
                          Configure your hospital branding, clinic address, geo-coordinates (latitude & longitude), contact info, logo, and printing preferences for clinical Rx slips.
                        </p>
                      </div>
                    </div>

                    {/* Prescription Hospital & Clinic Details Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-6">
                      <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                        <FileText className="w-5 h-5 text-primary-600" />
                        <h3 className="text-base font-bold text-secondary-900">Hospital & Clinic Information</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Hospital / Clinic Name
                          </label>
                          <input
                            type="text"
                            value={hospitalName}
                            onChange={(e) => setHospitalName(e.target.value)}
                            placeholder="e.g. NL Labs Hospital"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-secondary-900"
                          />
                          <p className="text-[11px] text-secondary-400 mt-1">
                            Appears as the primary bold title at top left.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Slogan / Specialty Tagline
                          </label>
                          <input
                            type="text"
                            value={hospitalSlogan}
                            onChange={(e) => setHospitalSlogan(e.target.value)}
                            placeholder="e.g. General Medicine & Outpatient Clinical Care"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-700"
                          />
                          <p className="text-[11px] text-secondary-400 mt-1">
                            Subtitle shown immediately under hospital name.
                          </p>
                        </div>
                      </div>

                      {/* Address takes the full row unless there's a GSTIN to
                          show (read-only, from the org record) beside it. */}
                      <div className={`grid grid-cols-1 gap-5 ${orgGstin ? "md:grid-cols-2" : ""}`}>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Full Clinic Address
                          </label>
                          <textarea
                            rows={2}
                            value={hospitalAddress}
                            onChange={(e) => setHospitalAddress(e.target.value)}
                            placeholder="e.g. Medical Enclave, Sector 4, Main Clinic Road, New Delhi 110029"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-700"
                          />
                          <p className="text-[11px] text-secondary-400 mt-1">
                            Complete clinic address printed on prescription slips.
                          </p>
                        </div>

                        {orgGstin && (
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                              GSTIN
                            </label>
                            <input
                              type="text"
                              value={orgGstin}
                              readOnly
                              disabled
                              className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-200 bg-secondary-50 text-sm font-mono font-semibold text-secondary-700 cursor-not-allowed"
                            />
                            <p className="text-[11px] text-secondary-400 mt-1">
                              View only - set during organization onboarding. Printed on the letterhead. Contact support to change it.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Geo Coordinates: Latitude & Longitude */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 p-4 bg-secondary-50/70 border border-secondary-200 rounded-xl">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-primary-600" />
                            Clinic Latitude (Lat)
                          </label>
                          <input
                            type="text"
                            value={hospitalLatitude}
                            onChange={(e) => setHospitalLatitude(e.target.value)}
                            placeholder="e.g. 28.5672"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-mono text-secondary-800"
                          />
                          <p className="text-[11px] text-secondary-400 mt-1">
                            GPS Latitude coordinate for map navigation & clinic location.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-primary-600" />
                            Clinic Longitude (Long)
                          </label>
                          <input
                            type="text"
                            value={hospitalLongitude}
                            onChange={(e) => setHospitalLongitude(e.target.value)}
                            placeholder="e.g. 77.2100"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-mono text-secondary-800"
                          />
                          <p className="text-[11px] text-secondary-400 mt-1">
                            GPS Longitude coordinate for map navigation & clinic location.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="text"
                            value={hospitalPhone}
                            onChange={(e) => setHospitalPhone(e.target.value)}
                            placeholder="e.g. +91 98765 43210"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={hospitalEmail}
                            onChange={(e) => setHospitalEmail(e.target.value)}
                            placeholder="e.g. care@mindconnecthealth.com"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                            Website
                          </label>
                          <input
                            type="text"
                            value={hospitalWebsite}
                            onChange={(e) => setHospitalWebsite(e.target.value)}
                            placeholder="e.g. www.mindconnecthealth.com"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-700"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Logo & Graphic Options */}
                    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-6">
                      <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                        <ImageIcon className="w-5 h-5 text-primary-600" />
                        <h3 className="text-base font-bold text-secondary-900">Prescription Header Logo</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Option 1: Default Vector Logo */}
                        <div
                          onClick={() => setLogoType("default")}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${logoType === "default"
                            ? "border-primary-500 bg-primary-50/40 shadow-sm"
                            : "border-secondary-200 hover:border-secondary-300 bg-white"
                            }`}
                        >
                          <input
                            type="radio"
                            name="logoType"
                            checked={logoType === "default"}
                            onChange={() => setLogoType("default")}
                            className="text-primary-600 focus:ring-primary-500 h-4 w-4"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-sm text-secondary-900">Standard Medical Vector Logo</div>
                            <div className="text-xs text-secondary-500 mt-0.5">
                              Stylized Stethoscope & Heart medical emblem matching clinical guidelines.
                            </div>
                          </div>
                          <div className="w-12 h-12 bg-white rounded-lg border border-primary-200 flex items-center justify-center p-1.5 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" className="w-full h-full text-primary-600 fill-current">
                              <path d="M68 106 C68 106, 114 80, 114 48 C114 30, 99 18, 83 18 C72 18, 66 25, 64 28 C62 25, 56 18, 45 18 C33 18, 22 28, 22 45 C22 55, 27 67, 35 78 C31 75, 27 70, 25 64 C23 72, 28 82, 36 89 C45 97, 57 103, 68 106 Z" fill="currentColor" />
                              <circle cx="75" cy="70" r="14" fill="#ffffff" />
                              <circle cx="75" cy="70" r="7" fill="currentColor" />
                              <path d="M75 84 C75 98, 48 108, 32 94 C18 82, 14 62, 18 45 C22 30, 32 20, 36 18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" />
                            </svg>
                          </div>
                        </div>

                        {/* Option 2: Custom Hospital Logo */}
                        <div
                          onClick={() => setLogoType("custom")}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${logoType === "custom"
                            ? "border-primary-500 bg-primary-50/40 shadow-sm"
                            : "border-secondary-200 hover:border-secondary-300 bg-white"
                            }`}
                        >
                          <input
                            type="radio"
                            name="logoType"
                            checked={logoType === "custom"}
                            onChange={() => setLogoType("custom")}
                            className="text-primary-600 focus:ring-primary-500 h-4 w-4"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-sm text-secondary-900">Custom Clinic / Hospital Logo</div>
                            <div className="text-xs text-secondary-500 mt-0.5">
                              Upload your hospital's custom brand emblem, seal, or logo (PNG, JPG, WEBP).
                            </div>
                          </div>
                          {logoPreview ? (
                            <img
                              src={logoPreview}
                              alt="Custom Logo"
                              className="w-12 h-12 object-contain bg-white rounded-lg border border-secondary-200 p-1 shadow-sm"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-secondary-100 rounded-lg border border-secondary-200 flex items-center justify-center text-secondary-400">
                              <ImageIcon className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      </div>

                      {logoType === "custom" && (
                        <div className="p-4 bg-secondary-50 rounded-xl border border-secondary-200 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                                Logo Image URL
                              </label>
                              <input
                                type="text"
                                value={isS3Logo ? "" : logoUrl}
                                onChange={(e) => { setLogoUrl(e.target.value); setLogoPreview(e.target.value); }}
                                placeholder={isS3Logo ? "Using your uploaded logo" : "https://example.com/logo.png"}
                                className="w-full px-3.5 py-2 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-secondary-700"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                                Or Upload Image File
                              </label>
                              <div className="flex items-center gap-2">
                                <label className="flex-1 px-4 py-2 bg-white border border-secondary-300 rounded-lg text-xs font-medium text-secondary-700 hover:bg-secondary-50 cursor-pointer flex items-center justify-center gap-2 shadow-sm">
                                  <UploadCloud className="w-4 h-4 text-primary-600" />
                                  <span>{logoUploading ? "Uploading…" : "Choose File (Max 1MB)"}</span>
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    disabled={logoUploading}
                                    onChange={handleLogoFileUpload}
                                    className="hidden"
                                  />
                                </label>
                                {logoUrl && (
                                  <button
                                    type="button"
                                    onClick={() => { setLogoUrl(""); setLogoPreview(""); }}
                                    className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {logoPreview && (
                            <div className="flex items-center gap-3 pt-2">
                              <span className="text-xs font-semibold text-secondary-600">Active Logo Preview:</span>
                              <div className="p-2 bg-white rounded-lg border border-secondary-200 shadow-sm inline-block">
                                <img
                                  src={logoPreview}
                                  alt="Logo Preview"
                                  className="max-h-16 max-w-40 object-contain"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Default Print Behavior */}
                    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-4">
                      <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                        <Printer className="w-5 h-5 text-primary-600" />
                        <h3 className="text-base font-bold text-secondary-900">Stationery & Print Mode Default</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                          onClick={() => setDefaultHideHeader(false)}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3 ${!defaultHideHeader
                            ? "border-primary-500 bg-primary-50/40 shadow-sm"
                            : "border-secondary-200 hover:border-secondary-300 bg-white"
                            }`}
                        >
                          <input
                            type="radio"
                            name="defaultHideHeader"
                            checked={!defaultHideHeader}
                            onChange={() => setDefaultHideHeader(false)}
                            className="text-primary-600 focus:ring-primary-500 h-4 w-4"
                          />
                          <div>
                            <div className="font-bold text-sm text-secondary-900">Print Full Letterhead (Default)</div>
                            <div className="text-xs text-secondary-500 mt-0.5">
                              Prints the full hospital header, clinic contacts, and logo on blank A4 paper.
                            </div>
                          </div>
                        </div>

                        <div
                          onClick={() => setDefaultHideHeader(true)}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3 ${defaultHideHeader
                            ? "border-primary-500 bg-primary-50/40 shadow-sm"
                            : "border-secondary-200 hover:border-secondary-300 bg-white"
                            }`}
                        >
                          <input
                            type="radio"
                            name="defaultHideHeader"
                            checked={defaultHideHeader}
                            onChange={() => setDefaultHideHeader(true)}
                            className="text-primary-600 focus:ring-primary-500 h-4 w-4"
                          />
                          <div>
                            <div className="font-bold text-sm text-secondary-900">Pre-Printed Pad Stationery (Hide Header)</div>
                            <div className="text-xs text-secondary-500 mt-0.5">
                              Hides the header and adds top spacing so it prints directly under physical pre-printed letterheads.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Real-time Interactive Letterhead Preview */}
                    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-4">
                      <div className="flex items-center justify-between border-b border-secondary-100 pb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-primary-600" />
                          <h3 className="text-base font-bold text-secondary-900">Live Letterhead Pad Preview</h3>
                        </div>
                        <span className="text-xs text-secondary-400 font-mono">210mm x 297mm Layout</span>
                      </div>

                      {/* Mockup Sheet */}
                      <div className="p-6 bg-white border-2 border-dashed border-secondary-300 rounded-xl shadow-inner relative overflow-hidden">
                        {defaultHideHeader ? (
                          <div className="h-28 bg-secondary-50 border border-dashed border-secondary-300 rounded-lg flex flex-col items-center justify-center text-secondary-400 mb-4">
                            <span className="text-xs font-bold uppercase tracking-wider text-secondary-500">
                              [ Pre-printed Letterhead Clearance Space ]
                            </span>
                            <span className="text-[11px] text-secondary-400 mt-1">
                              Hospital Header is hidden. Print starts directly from Patient Line.
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-between items-start border-b border-secondary-100 pb-3 mb-3">
                            <div className="space-y-1 max-w-md">
                              <div className="text-2xl font-extrabold text-primary-700 leading-tight">
                                {hospitalName || "NL Labs Hospital"}
                              </div>
                              <div className="text-xs font-semibold text-secondary-500">
                                {hospitalSlogan || "General Medicine & Outpatient Clinical Care"}
                              </div>
                              <div className="text-xs text-secondary-900 font-bold pt-0.5">
                                Dr. Munna Kumar
                              </div>
                              <div className="text-[10.5px] font-bold text-primary-600 uppercase tracking-wide">
                                Dentist
                              </div>
                              <div className="text-[10px] text-secondary-600 font-medium">
                                BDS, MDS &nbsp;•&nbsp; <span className="text-secondary-400 font-normal">Reg: DCI-45812</span>
                              </div>
                              <div className="text-[11px] text-secondary-600 leading-normal">
                                {hospitalAddress || "Medical Enclave, Sector 4, Main Clinic Road, New Delhi 110029"}
                              </div>
                            </div>

                            <div className="flex-shrink-0 text-right space-y-1.5 min-w-[190px]">
                              {logoType === "custom" && logoPreview ? (
                                <img
                                  src={logoPreview}
                                  alt="Preview Logo"
                                  className="h-14 max-w-32 object-contain ml-auto"
                                />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" className="w-14 h-14 text-primary-600 fill-current ml-auto">
                                  <path d="M68 106 C68 106, 114 80, 114 48 C114 30, 99 18, 83 18 C72 18, 66 25, 64 28 C62 25, 56 18, 45 18 C33 18, 22 28, 22 45 C22 55, 27 67, 35 78 C31 75, 27 70, 25 64 C23 72, 28 82, 36 89 C45 97, 57 103, 68 106 Z" fill="currentColor" />
                                  <circle cx="75" cy="70" r="14" fill="#ffffff" />
                                  <circle cx="75" cy="70" r="7" fill="currentColor" />
                                  <path d="M75 84 C75 98, 48 108, 32 94 C18 82, 14 62, 18 45 C22 30, 32 20, 36 18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" />
                                </svg>
                              )}
                              <div className="text-[9.5px] text-secondary-500 leading-tight space-y-0.5 border-t border-secondary-200 pt-1.5">
                                <div><span className="font-bold text-secondary-700">Ph:</span> {hospitalPhone || "+91 98765 43210"}</div>
                                <div><span className="font-bold text-secondary-700">Email:</span> {hospitalEmail || "care@mindconnecthealth.com"}</div>
                                <div><span className="font-bold text-secondary-700">Web:</span> {hospitalWebsite || "www.mindconnecthealth.com"}</div>
                                {orgGstin && <div><span className="font-bold text-secondary-700">GSTIN:</span> <span className="font-mono">{orgGstin}</span></div>}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Patient Line Mockup */}
                        <div className="flex items-baseline text-xs font-semibold text-secondary-700 gap-3 border-b border-secondary-200 pb-2 mb-3">
                          <span className="font-bold text-secondary-900">Name :</span>
                          <span className="font-bold text-secondary-800">John Doe</span>
                          <span className="flex-1 text-secondary-300 tracking-widest overflow-hidden">....................................................................................</span>
                          <span><strong className="text-secondary-900">Age:</strong> 32 Yrs</span>
                          <span><strong className="text-secondary-900">Sex:</strong> Male</span>
                          <span><strong className="text-secondary-900">Date:</strong> 09 Sep 2026</span>
                        </div>

                        {/* Rx Symbol */}
                        <div className="flex items-center gap-3 mb-4">
                          <span className="font-serif font-bold italic text-3xl text-primary-600 leading-none">℞</span>
                          <span className="text-[11px] font-bold bg-primary-50 text-primary-700 border border-primary-200 px-2 py-0.5 rounded">
                            PROVISIONAL DIAGNOSIS: Viral Gastroenteritis
                          </span>
                        </div>

                        {/* Faint Ruled Lines */}
                        <div className="space-y-3 mb-6">
                          <div className="border-b border-dotted border-secondary-200 h-2"></div>
                          <div className="border-b border-dotted border-secondary-200 h-2"></div>
                          <div className="border-b border-dotted border-secondary-200 h-2"></div>
                        </div>

                        {/* Footer Stripes Mockup */}
                        <div className="pt-4 mt-6">
                          <div className="h-1.5 bg-primary-600 w-full rounded-t-sm"></div>
                          <div className="h-3 bg-secondary-400 w-full rounded-b-sm"></div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Save Button */}
                    {isAdmin && (
                      <div className="mt-6 flex justify-end pt-4 border-t border-secondary-200">
                        <button
                          type="button"
                          onClick={handleSavePrescriptionSettings}
                          disabled={savingPrescription}
                          className="px-6 py-2.5 text-sm md:text-base font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-colors cursor-pointer"
                        >
                          <Save className="w-4 h-4" />
                          {savingPrescription ? "Saving..." : "Save Letterhead Settings"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {activeSection === "PatientAlerts" && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Patient alerts</h2>
                      <p className="text-sm md:text-base text-gray-500">How patients hear from your clinic when something needs them.</p>
                    </div>
                    <PatientAlertsCard />
                  </div>
                )}

                {activeSection === "Clinical" && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-1">Clinical & Safety</h2>
                      <p className="text-sm md:text-base text-gray-500">Who can read clinical notes, and how patient risk is recorded.</p>
                    </div>
                    <ClinicalPrivacyCard />
                    <NoteTemplatesCard />
                  </div>
                )}

                {activeSection === "FrameworkPrivacy" && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2.5 mb-1">
                        <h2 className="text-lg md:text-xl font-bold text-gray-800">
                          Framework & Privacy Settings
                        </h2>
                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-purple-100 text-purple-700 rounded border border-purple-200">
                          Psychology Only
                        </span>
                      </div>
                      <p className="text-sm md:text-base text-gray-500">
                        Configure clinical therapy standards, supervision modes, and report transcript confidentiality for psychology training.
                      </p>
                    </div>

                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-purple-900 text-xs md:text-sm flex items-start gap-3">
                      <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Notice for General Medicine Practices:</span> These settings specifically govern clinical psychotherapy simulation frameworks (CTS-R, NIMHANS) and counseling session transcripts. If your clinic only provides general medical appointments, default values are applied automatically.
                      </div>
                    </div>

                    {/* Interaction Settings */}
                    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                          <Shield className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-semibold text-gray-800">
                            Clinical Framework & Interaction
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            Configure the evaluation standard (CTS-R vs NIMHANS) and general behavior for psychology sessions.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4 pt-2">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">Evaluation Standard</label>
                          <select
                            value={evaluationStandard}
                            onChange={(e) => setEvaluationStandard(e.target.value)}
                            disabled={!isAdmin}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="CTS-R">CTS-R (Standard CBT)</option>
                            <option value="NIMHANS">NIMHANS (Institutional)</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500">Choosing NIMHANS enables institutional scoring and additional qualitative signals.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">Evaluation Mode</label>
                          <select
                            value={evaluationMode}
                            onChange={(e) => setEvaluationMode(e.target.value)}
                            disabled={!isAdmin}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Academic">Academic / Training</option>
                            <option value="Tele-MANAS">Tele-MANAS (Operational)</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500">Mode-specific constraints applied during clinical verification.</p>
                        </div>
                      </div>
                    </div>

                    {/* Privacy & Reports Settings */}
                    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                          <Brain className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-semibold text-gray-800">
                            Privacy & Reports
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            Configure what data is included in generated clinical evaluation reports.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={includeTranscriptInReport}
                            onChange={(e) => setIncludeTranscriptInReport(e.target.checked)}
                            disabled={!isAdmin}
                            className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            id="transcript-toggle"
                          />
                          <div>
                            <label htmlFor="transcript-toggle" className="text-sm md:text-base font-semibold text-gray-800 cursor-pointer">
                              Include Transcript in Report
                            </label>
                            <div className="text-xs md:text-sm text-gray-500 mt-1">
                              If enabled, the full conversation transcript will be included in the final PDF/JSON report.
                              <span className="block mt-1 text-red-600 font-medium">
                                Disable this to enforce stricter privacy (references by Turn Number only).
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isAdmin && (
                        <div className="mt-6 flex justify-end pt-4 border-t border-secondary-200">
                          <button
                            type="button"
                            onClick={handleSaveGeneral}
                            disabled={saving}
                            className="px-6 py-2.5 text-sm md:text-base font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-colors cursor-pointer"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? "Saving..." : "Save Framework & Privacy Settings"}
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                )}

                {activeSection === "Experience" && (
                  <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div>
                      <div className="flex items-center gap-2.5 mb-2">
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                          Experience & Complexity
                        </h2>
                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-purple-100 text-purple-700 rounded border border-purple-200">
                          Psychology Only
                        </span>
                      </div>
                      <p className="text-gray-500 text-lg">
                        Fine-tune how therapist experience levels are defined and how case difficulty impacts evaluation scores in psychology simulations.
                      </p>
                    </div>

                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-purple-900 text-xs md:text-sm flex items-start gap-3">
                      <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Notice for General Medicine Practices:</span> Experience tier calibrations and complexity weights are calibrated for psychotherapy patient simulations and supervisor rubrics.
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Experience Thresholds Card */}
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl">
                            <Clock className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">Career Timeline</h3>
                            <p className="text-sm text-gray-500">Years required for each level</p>
                          </div>
                        </div>

                        <div className="relative pl-4 border-l-2 border-indigo-100 space-y-8 py-2">
                          {/* Novice Node */}
                          <div className="relative">
                            <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-gray-300 ring-4 ring-white"></div>
                            <div className="mb-1">
                              <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">ENTRY LEVEL</span>
                            </div>
                            <h4 className="text-base font-bold text-gray-800">Novice Therapist</h4>
                            <p className="text-xs text-gray-500 mt-1">0 - {expThresholdIntermediate} Years</p>
                          </div>

                          {/* Intermediate Node */}
                          <div className="relative">
                            <div className="absolute -left-[23px] top-0 w-4 h-4 rounded-full bg-indigo-500 ring-4 ring-white shadow-sm"></div>
                            <div className="mb-2">
                              <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md uppercase tracking-wide">
                                Milestone 1
                              </span>
                            </div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Intermediate Threshold
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={expThresholdIntermediate}
                                onChange={(e) => setExpThresholdIntermediate(parseInt(e.target.value))}
                                disabled={!isAdmin}
                                className="w-20 px-3 py-1.5 text-sm font-bold border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <span className="text-sm text-gray-500 font-medium">Years</span>
                            </div>
                          </div>

                          {/* Expert Node */}
                          <div className="relative">
                            <div className="absolute -left-[23px] top-0 w-4 h-4 rounded-full bg-purple-600 ring-4 ring-white shadow-sm"></div>
                            <div className="mb-2">
                              <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-md uppercase tracking-wide">
                                Milestone 2
                              </span>
                            </div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Expert Threshold
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="30"
                                value={expThresholdExpert}
                                onChange={(e) => setExpThresholdExpert(parseInt(e.target.value))}
                                disabled={!isAdmin}
                                className="w-20 px-3 py-1.5 text-sm font-bold border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              />
                              <span className="text-sm text-gray-500 font-medium">Years</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Complexity Adjustments Card */}
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl">
                            <ShieldAlert className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">Difficulty Concession</h3>
                            <p className="text-sm text-gray-500">Score reduction for case complexity</p>
                          </div>
                        </div>

                        <div className="space-y-4 flex-1">
                          {/* Medium Widget */}
                          <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-100 rounded-xl p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-orange-500" />
                                <span className="font-bold text-orange-800 text-sm">Medium Complexity</span>
                              </div>
                              <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-bold">Standard Risk</span>
                            </div>

                            <div className="flex items-center gap-3 mt-3">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-orange-400 rounded-full transition-all duration-300"
                                  style={{ width: `${(complexityAdjustmentMedium / 20) * 100}%` }}
                                ></div>
                              </div>
                              <div className="flex items-center bg-white border border-gray-200 rounded-md px-2 py-1 shadow-sm">
                                <span className="text-red-500 font-bold mr-1">-</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="20"
                                  value={complexityAdjustmentMedium}
                                  onChange={(e) => setComplexityAdjustmentMedium(parseInt(e.target.value))}
                                  disabled={!isAdmin}
                                  className="w-12 p-0 text-center font-bold text-gray-800 border-none focus:ring-0 text-sm"
                                />
                                <span className="text-xs text-gray-400 ml-1">pts</span>
                              </div>
                            </div>
                          </div>

                          {/* High Widget */}
                          <div className="bg-gradient-to-br from-red-50 to-white border border-red-100 rounded-xl p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-red-500 fill-current" />
                                <span className="font-bold text-red-800 text-sm">High Complexity</span>
                              </div>
                              <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-bold">High Risk</span>
                            </div>

                            <div className="flex items-center gap-3 mt-3">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-red-500 rounded-full transition-all duration-300"
                                  style={{ width: `${(complexityAdjustmentHigh / 30) * 100}%` }}
                                ></div>
                              </div>
                              <div className="flex items-center bg-white border border-gray-200 rounded-md px-2 py-1 shadow-sm">
                                <span className="text-red-500 font-bold mr-1">-</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="50"
                                  value={complexityAdjustmentHigh}
                                  onChange={(e) => setComplexityAdjustmentHigh(parseInt(e.target.value))}
                                  disabled={!isAdmin}
                                  className="w-12 p-0 text-center font-bold text-gray-800 border-none focus:ring-0 text-sm"
                                />
                                <span className="text-xs text-gray-400 ml-1">pts</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2.5 bg-emerald-100 text-emerald-600 rounded-xl">
                          <Sparkles className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">Scaling Factors</h3>
                          <p className="text-sm text-gray-500">Pass rate of base requirement (%)</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Novice */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center relative overflow-hidden group">
                          <div className="absolute top-0 w-full h-1 bg-gray-300"></div>
                          <h4 className="font-bold text-gray-600 mb-4 mt-2">Novice</h4>
                          <div className="relative inline-flex items-center justify-center">
                            <svg className="w-24 h-24 transform -rotate-90">
                              <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-200" />
                              <circle
                                cx="48" cy="48" r="40"
                                stroke="currentColor" strokeWidth="8" fill="transparent"
                                strokeDasharray={251.2}
                                strokeDashoffset={251.2 - (251.2 * expectationNovice / 100)}
                                className="text-gray-500 transition-all duration-1000 ease-out"
                              />
                            </svg>
                            <div className="absolute flex flex-col items-center">
                              <input
                                type="number"
                                value={expectationNovice}
                                onChange={(e) => setExpectationNovice(parseInt(e.target.value))}
                                className="w-12 text-center bg-transparent text-xl font-bold text-gray-800 focus:outline-none focus:border-b-2 focus:border-gray-400"
                                disabled={!isAdmin}
                              />
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Scaling %</span>
                            </div>
                          </div>
                        </div>

                        {/* Intermediate */}
                        <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100 text-center relative overflow-hidden group">
                          <div className="absolute top-0 w-full h-1 bg-indigo-500"></div>
                          <h4 className="font-bold text-indigo-700 mb-4 mt-2">Intermediate</h4>
                          <div className="relative inline-flex items-center justify-center">
                            <svg className="w-24 h-24 transform -rotate-90">
                              <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-indigo-100" />
                              <circle
                                cx="48" cy="48" r="40"
                                stroke="currentColor" strokeWidth="8" fill="transparent"
                                strokeDasharray={251.2}
                                strokeDashoffset={251.2 - (251.2 * expectationIntermediate / 100)}
                                className="text-indigo-500 transition-all duration-1000 ease-out"
                              />
                            </svg>
                            <div className="absolute flex flex-col items-center">
                              <input
                                type="number"
                                value={expectationIntermediate}
                                onChange={(e) => setExpectationIntermediate(parseInt(e.target.value))}
                                className="w-12 text-center bg-transparent text-xl font-bold text-indigo-800 focus:outline-none focus:border-b-2 focus:border-indigo-400"
                                disabled={!isAdmin}
                              />
                              <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-tight">Scaling %</span>
                            </div>
                          </div>
                        </div>

                        {/* Expert */}
                        <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100 text-center relative overflow-hidden group">
                          <div className="absolute top-0 w-full h-1 bg-purple-600"></div>
                          <h4 className="font-bold text-purple-700 mb-4 mt-2">Expert</h4>
                          <div className="relative inline-flex items-center justify-center">
                            <svg className="w-24 h-24 transform -rotate-90">
                              <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-purple-100" />
                              <circle
                                cx="48" cy="48" r="40"
                                stroke="currentColor" strokeWidth="8" fill="transparent"
                                strokeDasharray={251.2}
                                strokeDashoffset={251.2 - (251.2 * expectationExpert / 100)}
                                className="text-purple-600 transition-all duration-1000 ease-out"
                              />
                            </svg>
                            <div className="absolute flex flex-col items-center">
                              <input
                                type="number"
                                value={expectationExpert}
                                onChange={(e) => setExpectationExpert(parseInt(e.target.value))}
                                className="w-12 text-center bg-transparent text-xl font-bold text-purple-800 focus:outline-none focus:border-b-2 focus:border-purple-400"
                                disabled={!isAdmin}
                              />
                              <span className="text-[10px] text-purple-400 font-bold uppercase tracking-tight">Scaling %</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phase-Specific Expectations */}
                    {Object.keys(availablePhases).length > 0 && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl">
                              <Activity className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-800">Phase-Specific Benchmarks</h3>
                              <p className="text-sm text-gray-500">Fine-tune pass marks for each therapy phase</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-8">
                          {["CBT", "REBT", "SFBT"]
                            .filter(type => availablePhases[type] && availablePhases[type].length > 0)
                            .map((therapyType) => (
                              <div key={therapyType} className="overflow-hidden border border-gray-100 rounded-xl">
                                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                  <h4 className="font-bold text-gray-700 flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                                    {therapyType} Implementation
                                  </h4>
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Expected Scores (%)</span>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left border-collapse">
                                    <thead>
                                      <tr className="border-b border-gray-50">
                                        <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase w-48">Therapy Phase</th>
                                        <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase">Clinical Summary</th>
                                        <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase text-center w-40">Base Requirement (%)</th>
                                        <th className="px-4 py-3 w-16"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(availablePhases[therapyType] || []).map((phase) => {
                                        const isOverridden = phaseExpectations[phase] !== undefined;
                                        const phaseValue = isOverridden
                                          ? (typeof phaseExpectations[phase] === 'object' ? phaseExpectations[phase].expert : phaseExpectations[phase])
                                          : 100;

                                        const phaseSummaries = {
                                          "Crisis": "High-risk management and immediate safety protocols.",
                                          "Stabilization": "Symptom management and building coping foundations.",
                                          "Change": "Core belief restructuring and behavioral intervention.",
                                          "Maintenance": "Relapse prevention and long-term skill integration.",
                                          "Default": "Routine therapy sessions and standard clinical practice."
                                        };

                                        return (
                                          <tr key={phase} className="hover:bg-gray-50/50 transition-colors border-b last:border-0 border-gray-50">
                                            <td className="px-4 py-3">
                                              <div className="font-bold text-gray-700 text-sm">{phase}</div>
                                              {isOverridden && <span className="text-[9px] font-bold text-blue-500 uppercase">Customized</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-500 italic">
                                              {phaseSummaries[phase] || "Phase-specific therapeutic interventions."}
                                            </td>
                                            <td className="px-4 py-3">
                                              <input
                                                type="number"
                                                value={phaseValue}
                                                onChange={(e) => {
                                                  const newVal = parseInt(e.target.value);
                                                  setPhaseExpectations(prev => ({
                                                    ...prev,
                                                    [phase]: newVal
                                                  }));
                                                }}
                                                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-center text-sm font-bold text-gray-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                                disabled={!isAdmin}
                                              />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                              {isOverridden && (
                                                <button
                                                  onClick={() => {
                                                    const newExpectations = { ...phaseExpectations };
                                                    delete newExpectations[phase];
                                                    setPhaseExpectations(newExpectations);
                                                  }}
                                                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                  title="Reset to global default"
                                                >
                                                  <Zap className="w-4 h-4" />
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {isAdmin && (
                      <div className="flex justify-end pt-4">
                        <button
                          type="button"
                          onClick={handleSaveAllConfig}
                          disabled={saving}
                          className="px-8 py-3 text-base font-bold bg-primary-600 text-white rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-500/25 transform transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                        >
                          <Save className="w-5 h-5" />
                          {saving ? "Saving Changes..." : "Save Configuration"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
